// @ts-nocheck
import { supabase } from './supabase';
import TherapistRegister from './TherapistRegister';
import {
  isSupabaseReady,
  fetchClients, fetchMemories, addMemory, deleteMemory,
  fetchDiary, addDiaryEntry,
  fetchContributions, addContribution, markContributionRead,
  uploadFile, saveSessionNote,
} from './supabase';
import { useState, useRef, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const T = {
  primary:"#3A6EA5", primaryL:"#EBF2FA", primaryD:"#2C5580",
  green:"#7FB069",   greenL:"#EFF7EC",   greenD:"#5A8A48",
  amber:"#D97706",   amberL:"#FEF3C7",
  danger:"#DC2626",  dangerL:"#FEE2E2",
  bg:"#EBEBEB",      surface:"#FFFFFF",  surfaceAlt:"#F5F5F5",
  border:"#DEDEDE",  borderD:"#C8C8C8",
  text:"#1A1A2E",    textSub:"#3A3A4A",
  muted:"#6B6B7A",   mutedL:"#9A9AAA",
  sidebar:"#52525E", sidebarAct:"#6A6A7A",
  family:"#8B6FCB",
  tag:{ loss:"#7C6DB3",achievement:"#7FB069",trauma:"#DC2626",love:"#DB7093",childhood:"#3A6EA5",work:"#D97706" }
};

// ─────────────────────────────────────────────────────────────
// GLOBAL STYLES — responsive grid classes + animations
// ─────────────────────────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
    *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
    html,body,#root { height:100%; }
    body { background:${T.bg}; color:${T.text}; font-family:'DM Sans',system-ui,sans-serif; -webkit-font-smoothing:antialiased; }

    /* ── Animations ── */
    @keyframes fadeUp   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
    @keyframes slideIn  { from{opacity:0;transform:translateX(-100%)} to{opacity:1;transform:translateX(0)} }
    @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.35} }
    .fade-up   { animation:fadeUp  .3s cubic-bezier(.4,0,.2,1); }
    .fade-in   { animation:fadeIn  .25s ease; }
    .slide-in  { animation:slideIn .28s cubic-bezier(.4,0,.2,1); }
    .pulse     { animation:pulse 2s ease infinite; }

    /* ── Scrollbar ── */
    ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:#C8C8C8;border-radius:4px}
    input::placeholder,textarea::placeholder{color:${T.mutedL}}
    input,textarea,select,button{font-family:'DM Sans',system-ui,sans-serif}

    /* ── Desktop-first layout ── */
    .dash-layout  { display:flex; height:100vh; overflow:hidden; }
    .dash-sidebar { width:240px; height:100%; flex-shrink:0; transition:transform .28s cubic-bezier(.4,0,.2,1); }
    .dash-content { flex:1; min-height:0; overflow-y:auto; display:flex; flex-direction:column; min-width:0; }
    .dash-topbar  { position:sticky; top:0; z-index:10; flex-shrink:0; }
    .dash-pad     { padding:28px 32px; }
    .sidebar-overlay { display:none; }
    .login-layout { display:grid; grid-template-columns:1fr 1fr; min-height:100vh; }
    .roles-grid   { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; max-width:760px; width:100%; }

    /* ── Responsive grid helpers (desktop) ── */
    .grid-4     { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
    .grid-3     { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
    .grid-2     { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; }
    .grid-2-1   { display:grid; grid-template-columns:2fr 1fr; gap:14px; }
    .grid-1-1   { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    .grid-proto { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
    .grid-phases{ display:grid; grid-template-columns:repeat(5,1fr); gap:3px; }

    /* ── Tablet (769–1100px): 2 kolon, dar sidebar ── */
    @media(min-width:769px) and (max-width:1100px){
      .dash-sidebar { width:200px !important; }
      .grid-4  { grid-template-columns:repeat(2,1fr) !important; }
      .grid-3  { grid-template-columns:repeat(2,1fr) !important; }
      .roles-grid { grid-template-columns:repeat(2,1fr) !important; }
      .dash-pad { padding:20px 24px; }
    }

    /* ── Hover cards ── */
    .role-card:hover { transform:translateY(-4px); }
    .nav-btn:hover   { background:rgba(255,255,255,.06) !important; color:rgba(255,255,255,.8) !important; }

    /* ── Mobil (<768px): hamburger menü ── */
    @media(max-width:768px){
      .dash-layout  { flex-direction:column; height:auto; min-height:100vh; overflow:visible; }
      .dash-sidebar { position:fixed !important; left:0; top:0; bottom:0; z-index:100;
                      transform:translateX(-100%); width:260px !important; height:100vh !important; }
      .dash-sidebar.open { transform:translateX(0) !important; }
      .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:99; }
      .sidebar-overlay.open { display:block !important; }
      .dash-content { height:auto; flex:unset; min-height:100vh; }
      .dash-pad     { padding:16px !important; }

      .grid-4,.grid-3,.grid-2,.grid-2-1,.grid-1-1,.grid-proto,.grid-phases { grid-template-columns:1fr !important; }
      .roles-grid   { grid-template-columns:1fr !important; max-width:420px; }
      .login-layout { grid-template-columns:1fr !important; }
      .login-left   { display:none !important; }

      .mobile-full  { width:100% !important; }
      .hide-mobile  { display:none !important; }
      .show-mobile  { display:flex !important; }
    }
    @media(min-width:769px){
      .show-mobile { display:none !important; }
    }
  `}</style>
);

// ─────────────────────────────────────────────────────────────
// PRIMITIVE COMPONENTS
// ─────────────────────────────────────────────────────────────
const Badge = ({ children, color=T.primary, small=false }:any) => (
  <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:color+"18",color,border:`1px solid ${color}30`,padding:small?"1px 8px":"3px 10px",borderRadius:20,fontSize:small?10:11,fontWeight:600,letterSpacing:.3,whiteSpace:"nowrap" }}>{children}</span>
);

const Chip = ({ children, active, color, onClick }:any) => (
  <button onClick={onClick} style={{ padding:"5px 14px",borderRadius:20,border:`1.5px solid ${active?color:T.border}`,background:active?color+"15":T.surface,color:active?color:T.muted,fontSize:12,fontWeight:600,transition:"all .2s",cursor:"pointer",whiteSpace:"nowrap" }}>{children}</button>
);

const Btn = ({ children, variant="primary", size="md", onClick, disabled, style:s, className="" }:any) => {
  const vs:any = { primary:{background:T.primary,color:"#fff",border:"none"}, outline:{background:"transparent",color:T.primary,border:`1.5px solid ${T.primary}`}, ghost:{background:"transparent",color:T.muted,border:`1.5px solid ${T.border}`}, danger:{background:T.dangerL,color:T.danger,border:`1.5px solid ${T.danger}30`}, success:{background:T.greenL,color:T.greenD,border:`1.5px solid ${T.green}40`} };
  const ss:any = { sm:{padding:"6px 14px",fontSize:12,borderRadius:8}, md:{padding:"9px 20px",fontSize:13,borderRadius:10}, lg:{padding:"12px 28px",fontSize:14,borderRadius:12} };
  return <button onClick={onClick} disabled={disabled} className={className} style={{ ...vs[variant],...ss[size],fontWeight:600,letterSpacing:.2,transition:"all .2s",opacity:disabled?.45:1,display:"inline-flex",alignItems:"center",gap:6,whiteSpace:"nowrap",cursor:disabled?"not-allowed":"pointer",...s }}>{children}</button>;
};

const Card = ({ children, style:s, noPad, className="" }:any) => (
  <div className={className} style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:noPad?0:"18px 20px",boxShadow:"0 1px 4px rgba(30,45,61,.05)",...s }}>{children}</div>
);

const SectionTitle = ({ children, sub }:any) => (
  <div style={{ marginBottom:18 }}>
    <h2 style={{ fontSize:19,fontWeight:600,color:T.text,margin:0,letterSpacing:-.3 }}>{children}</h2>
    {sub&&<p style={{ fontSize:12,color:T.muted,marginTop:4,lineHeight:1.6 }}>{sub}</p>}
  </div>
);

const Label = ({ children }:any) => (
  <label style={{ fontSize:10,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:.6,textTransform:"uppercase" }}>{children}</label>
);

const Inp = ({ disabled, style:s, ...p }:any) => (
  <input {...p} disabled={disabled} style={{ width:"100%",border:`1.5px solid ${disabled?T.border:T.borderD}`,borderRadius:9,padding:"9px 12px",fontSize:13,color:T.text,background:disabled?T.surfaceAlt:T.surface,transition:"border-color .2s",...s }} />
);

const Txta = ({ style:s, ...p }:any) => (
  <textarea {...p} style={{ width:"100%",border:`1.5px solid ${T.borderD}`,borderRadius:9,padding:"9px 12px",fontSize:13,color:T.text,background:T.surface,resize:"vertical",lineHeight:1.7,...s }} />
);

const StatusDot = ({ color, pulse=false }:any) => (
  <span className={pulse?"pulse":""} style={{ display:"inline-block",width:7,height:7,borderRadius:"50%",background:color,flexShrink:0 }} />
);

const TrustBar = () => (
  <div style={{ display:"flex",alignItems:"center",justifyContent:"center",flexWrap:"wrap",gap:20,padding:"11px 16px",borderTop:`1px solid ${T.border}`,background:T.surfaceAlt,flexShrink:0 }}>
    {[{c:T.green,t:"Güvenli Bağlantı"},{c:T.primary,t:"KVKK / GDPR"},{c:T.amber,t:"Seans Gizliliği"}].map((x,i)=>(
      <div key={i} style={{ display:"flex",alignItems:"center",gap:6 }}>
        <StatusDot color={x.c} />
        <span style={{ fontSize:11,color:T.muted,fontWeight:500 }}>{x.t}</span>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// SIDEBAR
// ─────────────────────────────────────────────────────────────
const Sidebar = ({ nav, active, onNav, user, role, photo, onLogout, meetActive, badge, open, onClose }:any) => {
  const accent = role==="therapist"?T.green:role==="client"?T.primary:T.family;
  const roleLabel:any = { therapist:"Terapist", client:"Danışan", family:"Aile Üyesi" };
  return (
    <>
      {/* Overlay (mobile) */}
      <div className={`sidebar-overlay${open?" open":""}`} onClick={onClose} />

      <div className={`dash-sidebar${open?" open":""}`} style={{ width:220,minHeight:0,height:"100%",background:T.sidebar,display:"flex",flexDirection:"column",flexShrink:0,overflow:"hidden" }}>
        {/* Logo */}
        <div style={{ padding:"20px 18px 14px",borderBottom:"1px solid rgba(255,255,255,.07)" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
            <div style={{ fontSize:20,fontWeight:700,color:"#fff",letterSpacing:-.5 }}>Anımsam<span style={{ color:accent }}>A</span></div>
            <button className="show-mobile" onClick={onClose} style={{ background:"none",border:"none",color:"rgba(255,255,255,.4)",fontSize:18,cursor:"pointer",padding:0,lineHeight:1 }}>✕</button>
          </div>
          <div style={{ fontSize:9,color:"rgba(255,255,255,.3)",marginTop:3,letterSpacing:1.2,textTransform:"uppercase" }}>Çevrimiçi Terapi Platformu</div>
          <div onClick={()=>{onNav("profile");onClose?.();}} style={{ marginTop:14,display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,.05)",borderRadius:10,padding:"8px 10px",cursor:"pointer" }}>
            <div style={{ width:30,height:30,borderRadius:"50%",background:accent+"40",border:`2px solid ${accent}60`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0 }}>
              {photo?<img src={photo} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>:<span style={{ fontSize:13,color:accent,fontWeight:700 }}>{user?.name?.[0]||"U"}</span>}
            </div>
            <div>
              <div style={{ fontSize:12,fontWeight:600,color:"#fff",lineHeight:1.3 }}>{user?.name||"Kullanıcı"}</div>
              <div style={{ fontSize:10,color:"rgba(255,255,255,.4)" }}>{roleLabel[role]}</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1,padding:"10px 8px",overflowY:"auto" }}>
          {nav.map((item:any)=>{
            const isActive = active===item.id;
            return (
              <button key={item.id} className="nav-btn" onClick={()=>{onNav(item.id);onClose?.();}} style={{ display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",borderRadius:9,border:"none",marginBottom:2,background:isActive?T.sidebarAct:"transparent",color:isActive?"#fff":"rgba(255,255,255,.4)",fontWeight:isActive?600:400,fontSize:13,textAlign:"left",transition:"all .18s",cursor:"pointer",position:"relative" }}>
                <span style={{ fontSize:14,opacity:isActive?1:.6 }}>{item.icon}</span>
                <span style={{ flex:1 }}>{item.label}</span>
                {item.id==="meet"&&meetActive&&<StatusDot color={T.green} pulse />}
                {item.badge&&badge>0&&<span style={{ background:T.danger,color:"#fff",fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:10 }}>{badge}</span>}
                {isActive&&<span style={{ position:"absolute",left:0,top:"50%",transform:"translateY(-50%)",width:3,height:18,background:accent,borderRadius:"0 3px 3px 0" }}/>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding:"10px 8px 16px",borderTop:"1px solid rgba(255,255,255,.07)" }}>
          <button onClick={onLogout} style={{ width:"100%",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:8,padding:"7px 0",color:"rgba(255,255,255,.3)",fontSize:12,fontWeight:500,cursor:"pointer" }}>Oturumu Kapat</button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// TOP BAR
// ─────────────────────────────────────────────────────────────
const TopBar = ({ title, sub, meetCode, onHamburger }:any) => (
  <div className="dash-topbar" style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 20px",background:T.surface,borderBottom:`1px solid ${T.border}`,position:"sticky",top:0,zIndex:10,flexShrink:0 }}>
    <div style={{ display:"flex",alignItems:"center",gap:12 }}>
      <button className="show-mobile" onClick={onHamburger} style={{ background:"none",border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 8px",cursor:"pointer",color:T.muted,fontSize:16,lineHeight:1 }}>☰</button>
      <div>
        <div style={{ fontSize:15,fontWeight:600,color:T.text }}>{title}</div>
        {sub&&<div style={{ fontSize:11,color:T.muted,marginTop:1 }} className="hide-mobile">{sub}</div>}
      </div>
    </div>
    <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
      {meetCode&&<Badge color={T.primary}><StatusDot color={T.primary} pulse/> Aktif Görüşme</Badge>}
      <Badge color={T.green} ><StatusDot color={T.green}/> Güvenli</Badge>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MEETING CENTER
// ─────────────────────────────────────────────────────────────
const MeetingCenter = ({ role, sharedCode, onCodeSet, userCode }:any) => {
  const [inputCode,setInputCode] = useState("");
  const [activeCode,setActiveCode] = useState(sharedCode||"");
  const [notes,setNotes] = useState("");
  const [timer,setTimer] = useState(0); const [running,setRunning] = useState(false);
  const [copied,setCopied] = useState(false);
  const [popupWin,setPopupWin] = useState<any>(null);
  const timerRef = useRef<any>(null);
  const isTherapist = role==="therapist";

  const fmt=(s:number)=>`${String(Math.floor(s/3600)).padStart(2,"0")}:${String(Math.floor((s%3600)/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  // URL veya kod → saf kodu çıkar
  const extractCode = (raw:string) => {
    const trimmed = raw.trim();
    // https://meet.google.com/abc-defg-hij → abc-defg-hij
    const urlMatch = trimmed.match(/meet\.google\.com\/([a-z0-9-]+)/i);
    if(urlMatch) return urlMatch[1].toLowerCase();
    return trimmed.toLowerCase().replace(/\s+/g,"-");
  };

  const setCode=()=>{
    if(!inputCode.trim()) return;
    const c = extractCode(inputCode);
    setActiveCode(c); onCodeSet?.(c); setInputCode("");
  };

  const toggleTimer=()=>{ if(!running){timerRef.current=setInterval(()=>setTimer(t=>t+1),1000);setRunning(true);}else{clearInterval(timerRef.current);setRunning(false);} };

  // Popup pencerede aç — sayfayı kapatmaz
  const openMeet = () => {
    if(!activeCode) return;
    const url = `https://meet.google.com/${activeCode}`;
    const w = Math.min(1100, window.screen.availWidth - 100);
    const h = Math.min(750, window.screen.availHeight - 100);
    const left = Math.round((window.screen.availWidth - w) / 2);
    const top = Math.round((window.screen.availHeight - h) / 2);
    const win = window.open(url, "google_meet_animsama",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no`
    );
    setPopupWin(win);
  };

  return (
    <div className="fade-up">
      <SectionTitle>Google Meet Görüşmesi</SectionTitle>

      {/* Katılım kodu kutusu — tüm roller için */}
      {userCode && (
        <div style={{ background:`linear-gradient(135deg,${T.primaryD},${T.primary})`,borderRadius:14,padding:"16px 20px",marginBottom:18,color:"#fff" }}>
          <div style={{ fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:600,letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>
            {role==="client" ? "Danışan Panel Kodunuz" : role==="family" ? "Aile Erişim Kodunuz" : "Görüşme Kodu"}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:22,fontWeight:700,letterSpacing:3 }}>{userCode}</div>
            <button onClick={()=>navigator.clipboard?.writeText(userCode).catch(()=>{})} style={{ background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,padding:"6px 12px",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit" }}>
              Kopyala ⎘
            </button>
          </div>
          <div style={{ fontSize:11,color:"rgba(255,255,255,.5)",marginTop:6 }}>Bu kodu terapistinize bildirin</div>
        </div>
      )}

      <div style={{ background:T.primaryL,border:`1px solid ${T.primary}25`,borderRadius:12,padding:"12px 16px",marginBottom:18,display:"flex",gap:12,alignItems:"center" }}>
        <div style={{ width:36,height:36,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,color:"#fff" }}>🎥</div>
        <div>
          <div style={{ fontWeight:600,fontSize:13,color:T.primaryD }}>Görüşme ayrı pencerede açılır</div>
          <div style={{ fontSize:12,color:T.muted,marginTop:2,lineHeight:1.6 }}>AnımsamA arka planda çalışmaya devam eder. Google Meet kodu veya tam bağlantı girin.</div>
        </div>
      </div>

      <div className="grid-1-1">
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <Card noPad>
            <div style={{ background:`linear-gradient(135deg,${T.primaryD},${T.primary})`,padding:"16px 18px",borderRadius:"13px 13px 0 0" }}>
              <div style={{ fontSize:10,color:"rgba(255,255,255,.5)",fontWeight:600,letterSpacing:1,textTransform:"uppercase",marginBottom:5 }}>Aktif Görüşme Kodu</div>
              {activeCode
                ? <div style={{ fontFamily:"'DM Mono',monospace",fontSize:20,fontWeight:700,color:"#fff",letterSpacing:3 }}>{activeCode}</div>
                : <div style={{ fontSize:13,color:"rgba(255,255,255,.35)",fontStyle:"italic" }}>Henüz kod girilmedi</div>
              }
            </div>
            <div style={{ padding:"14px 18px" }}>
              {isTherapist ? (
                <div>
                  <div style={{ fontSize:11,color:T.muted,marginBottom:8 }}>Meet kodu veya tam link girin:</div>
                  <div style={{ display:"flex",gap:8 }}>
                    <Inp value={inputCode} onChange={(e:any)=>setInputCode(e.target.value)}
                      onKeyDown={(e:any)=>e.key==="Enter"&&setCode()}
                      placeholder="abc-defg-hij veya meet.google.com/..." style={{ flex:1 }}/>
                    <Btn onClick={setCode}>Ayarla</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize:13,color:T.muted,lineHeight:1.7 }}>
                  {activeCode ? "✓ Terapistiniz görüşme kodunu paylaştı." : "⏳ Görüşme kodu bekleniyor..."}
                </div>
              )}
            </div>
          </Card>

          <button onClick={openMeet} disabled={!activeCode} style={{ background:activeCode?T.primary:T.border,color:activeCode?"#fff":T.muted,border:"none",borderRadius:12,padding:"13px 0",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all .2s",opacity:activeCode?1:.6,cursor:activeCode?"pointer":"default",width:"100%" }}>
            🎥 Görüşmeye Katıl (Yeni Pencere)
          </button>

          <div style={{ display:"flex",gap:8 }}>
            <Btn variant="outline" size="sm" style={{ flex:1 }} onClick={()=>{ navigator.clipboard?.writeText(activeCode||"").catch(()=>{}); setCopied(true); setTimeout(()=>setCopied(false),2000); }}>
              {copied?"✓ Kopyalandı":"Kodu Kopyala"}
            </Btn>
            {isTherapist&&activeCode&&(
              <Btn variant="success" size="sm" style={{ flex:1 }} onClick={()=>navigator.clipboard?.writeText(`AnımsamA Görüşme\nKod: ${activeCode}\nhttps://meet.google.com/${activeCode}`).catch(()=>{})}>
                Davet Kopyala
              </Btn>
            )}
          </div>
        </div>
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <Card>
            <div style={{ fontWeight:600,fontSize:13,marginBottom:12,color:T.textSub }}>Seans Kronometresi</div>
            <div style={{ textAlign:"center",padding:"12px 0" }}>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:36,fontWeight:500,color:running?T.primary:T.text,letterSpacing:2 }}>{fmt(timer)}</div>
              <div style={{ display:"flex",gap:8,justifyContent:"center",marginTop:12 }}>
                <Btn variant={running?"danger":"primary"} size="sm" onClick={toggleTimer}>{running?"⏸ Duraklat":"▶ Başlat"}</Btn>
                <Btn variant="ghost" size="sm" onClick={()=>{ clearInterval(timerRef.current);setRunning(false);setTimer(0); }}>Sıfırla</Btn>
              </div>
            </div>
          </Card>
          <Card style={{ flex:1 }}>
            <div style={{ fontWeight:600,fontSize:13,marginBottom:10,color:T.textSub }}>Anlık Notlar</div>
            <Txta value={notes} onChange={(e:any)=>setNotes(e.target.value)} placeholder="Görüşme sırasında notlarınız..." style={{ minHeight:90 }}/>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// VOICE RECORDER
// ─────────────────────────────────────────────────────────────
const VoiceRecorder = ({ onSave }:any) => {
  const [state,setState]=useState("idle"); const [secs,setSecs]=useState(0); const [recs,setRecs]=useState<any[]>([]); const [label,setLabel]=useState(""); const [blobUrl,setBlobUrl]=useState<string|null>(null);
  const timerRef=useRef<any>(null); const mediaRef=useRef<any>(null); const chunksRef=useRef<any[]>([]);
  const fmt=(s:number)=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const startRec=async()=>{
    try{ const stream=await navigator.mediaDevices.getUserMedia({audio:true}); const mr=new MediaRecorder(stream); mediaRef.current=mr; chunksRef.current=[]; mr.ondataavailable=(e:any)=>chunksRef.current.push(e.data); mr.onstop=()=>{ const blob=new Blob(chunksRef.current,{type:"audio/webm"}); setBlobUrl(URL.createObjectURL(blob)); setState("preview"); stream.getTracks().forEach(t=>t.stop()); }; mr.start(); }
    catch{ setTimeout(()=>{ clearInterval(timerRef.current); setState("preview"); setBlobUrl(null); },3000); }
    setState("recording"); setSecs(0); timerRef.current=setInterval(()=>setSecs(s=>s+1),1000);
  };
  const stopRec=()=>{ clearInterval(timerRef.current); if(mediaRef.current&&mediaRef.current.state!=="inactive")mediaRef.current.stop(); else setState("preview"); };
  const saveRec=()=>{ const e={id:Date.now(),label:label||`Sesli Mesaj – ${new Date().toLocaleDateString("tr-TR")}`,url:blobUrl,dur:secs,date:new Date().toLocaleDateString("tr-TR")}; setRecs(r=>[e,...r]); onSave?.(e); setState("idle"); setSecs(0); setBlobUrl(null); setLabel(""); };
  return (
    <div>
      <Card style={{ background:`linear-gradient(135deg,${T.sidebar},${T.sidebarAct})`,border:"none",marginBottom:14 }}>
        <div style={{ textAlign:"center",padding:"8px 0" }}>
          {state==="idle"&&(<div className="fade-in"><div style={{ fontSize:14,fontWeight:600,color:"#fff",marginBottom:4 }}>Sesli Mesaj Kaydet</div><div style={{ fontSize:12,color:"rgba(255,255,255,.4)",marginBottom:20,lineHeight:1.6 }}>Kayıt terapist ve danışana iletilir.</div><button onClick={startRec} style={{ width:56,height:56,borderRadius:"50%",background:T.primary,color:"#fff",border:"none",fontSize:20,boxShadow:`0 4px 20px ${T.primary}60`,cursor:"pointer" }}>⏺</button></div>)}
          {state==="recording"&&(<div className="fade-in"><div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10 }}><StatusDot color="#EF4444" pulse/><span style={{ fontFamily:"'DM Mono',monospace",fontSize:28,fontWeight:500,color:"#fff" }}>{fmt(secs)}</span></div><div style={{ fontSize:12,color:"rgba(255,255,255,.4)",marginBottom:16 }}>Kaydediliyor...</div><button onClick={stopRec} style={{ width:44,height:44,borderRadius:"50%",background:"#EF4444",color:"#fff",border:"none",fontSize:16,cursor:"pointer" }}>⏹</button></div>)}
          {state==="preview"&&(<div className="fade-in"><div style={{ fontSize:13,fontWeight:600,color:T.green,marginBottom:10 }}>✓ Kayıt Tamamlandı ({fmt(secs)})</div>{blobUrl&&<audio controls src={blobUrl} style={{ width:"100%",marginBottom:10,borderRadius:8 }}/>}<Inp value={label} onChange={(e:any)=>setLabel(e.target.value)} placeholder="Başlık (opsiyonel)" style={{ marginBottom:10,background:"rgba(255,255,255,.1)",border:"1px solid rgba(255,255,255,.2)",color:"#fff" }}/><div style={{ display:"flex",gap:8,justifyContent:"center" }}><Btn variant="ghost" size="sm" style={{ color:"rgba(255,255,255,.6)",border:"1px solid rgba(255,255,255,.2)" }} onClick={()=>{ setState("idle");setSecs(0);setBlobUrl(null); }}>Tekrar</Btn><Btn size="sm" onClick={saveRec}>Gönder</Btn></div></div>)}
        </div>
      </Card>
      {recs.map((r:any)=>(
        <div key={r.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:T.surface,border:`1px solid ${T.border}`,borderLeft:`3px solid ${T.primary}`,borderRadius:10,marginBottom:8 }}>
          <div style={{ color:T.primary,fontSize:18 }}>🎙️</div>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontWeight:600,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{r.label}</div>
            <div style={{ fontSize:11,color:T.muted,marginTop:1 }}>{r.date} · {fmt(r.dur||0)}</div>
            {r.url&&<audio src={r.url} controls style={{ height:26,marginTop:6,width:"100%" }}/>}
          </div>
          <Badge color={T.green}>İletildi</Badge>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MEMORY MODAL
// ─────────────────────────────────────────────────────────────
const MemModal = ({ onSave, onClose, ageGroup }:any) => {
  const [f,setF]=useState<any>({title:"",date:"",valence:0,notes:"",tags:[],media:[]});
  const fileRef=useRef<any>();
  const tags=["childhood","love","loss","trauma","achievement","work"];
  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(15,25,40,.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }} className="fade-in">
      <Card style={{ width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
          <div><div style={{ fontWeight:600,fontSize:15 }}>Yeni Anı</div><div style={{ fontSize:11,color:T.muted,marginTop:2 }}>{ageGroup} · Dönem</div></div>
          <button onClick={onClose} style={{ width:28,height:28,borderRadius:8,background:T.surfaceAlt,border:`1px solid ${T.border}`,fontSize:15,color:T.muted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
        </div>
        <div className="grid-2-1" style={{ marginBottom:12 }}>
          <div><Label>Başlık</Label><Inp value={f.title} onChange={(e:any)=>setF((x:any)=>({...x,title:e.target.value}))} placeholder="Anı başlığı..."/></div>
          <div><Label>Yıl</Label><Inp value={f.date} onChange={(e:any)=>setF((x:any)=>({...x,date:e.target.value}))} placeholder="1975"/></div>
        </div>
        <div style={{ marginBottom:12 }}>
          <Label>Duygusal Değer: {f.valence>0?"+":""}{f.valence}</Label>
          <input type="range" min={-5} max={5} step={1} value={f.valence} onChange={(e:any)=>setF((x:any)=>({...x,valence:+e.target.value}))} style={{ width:"100%",accentColor:f.valence>=0?T.green:T.danger,marginTop:6 }}/>
          <div style={{ display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,marginTop:3 }}><span>−5</span><span>0</span><span>+5</span></div>
        </div>
        <div style={{ marginBottom:12 }}>
          <Label>Etiketler</Label>
          <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginTop:5 }}>
            {tags.map(t=><Chip key={t} active={f.tags.includes(t)} color={T.tag[t as keyof typeof T.tag]} onClick={()=>setF((x:any)=>({...x,tags:x.tags.includes(t)?x.tags.filter((i:string)=>i!==t):[...x.tags,t]}))}>{t}</Chip>)}
          </div>
        </div>
        <div style={{ marginBottom:12 }}><Label>Not</Label><Txta value={f.notes} onChange={(e:any)=>setF((x:any)=>({...x,notes:e.target.value}))} placeholder="Açıklama..." style={{ minHeight:60 }}/></div>
        <div style={{ marginBottom:16 }}>
          <Label>Medya Ekle</Label>
          <button onClick={()=>fileRef.current.click()} style={{ width:"100%",border:`2px dashed ${T.border}`,borderRadius:10,padding:12,textAlign:"center",background:T.surfaceAlt,cursor:"pointer",fontSize:12,color:T.muted,marginTop:5 }}>Fotoğraf / Ses / Belge</button>
          <input ref={fileRef} type="file" style={{ display:"none" }} onChange={(e:any)=>{ const file=e.target.files[0]; if(file)setF((x:any)=>({...x,media:[...x.media,{name:file.name}]})); }}/>
          {f.media.length>0&&<div style={{ fontSize:11,color:T.green,marginTop:6 }}>✓ {f.media.length} dosya</div>}
        </div>
        <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={async()=>{
            if(!f.title) return;
            try {
              const saved = await addMemory({ client_id:1, age_range:ageGroup, title:f.title, valence:f.valence, tags:f.tags, year:f.date, notes:f.notes });
              onSave(saved);
            } catch(e) {
              onSave({...f,id:Date.now(),age_range:ageGroup});
            }
            onClose();
          }}>Kaydet</Btn>
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PROFILE PANEL
// ─────────────────────────────────────────────────────────────
const ProfilePanel = ({ role, userName, accent, onPhotoChange, currentPhoto }:any) => {
  const photoRef=useRef<any>(); const docRef=useRef<any>();
  const [photo,setPhoto]=useState(currentPhoto||null); const [editing,setEditing]=useState(false); const [saved,setSaved]=useState(false); const [docs,setDocs]=useState<any[]>([]);
  const FIELDS:any={
    therapist:{label:"Terapist Profili",sections:[
      {title:"Kişisel Bilgiler",fields:[{key:"fullName",label:"Ad Soyad",defaultVal:userName},{key:"title",label:"Ünvan",placeholder:"Doç. Dr. / Uzm. Psikolog"},{key:"institution",label:"Kurum",placeholder:"Kurum adı"},{key:"department",label:"Bölüm",placeholder:"Sosyal Hizmet / Psikoloji"},{key:"email",label:"E-Posta",type:"email"},{key:"phone",label:"Telefon"}]},
      {title:"Uzmanlık & Klinik",fields:[{key:"specialization",label:"Uzmanlık Alanı",placeholder:"Anımsama Terapisi"},{key:"approach",label:"Terapötik Yaklaşım"},{key:"license",label:"Lisans No"},{key:"research",label:"Araştırma Alanı"}]},
      {title:"Hakkında",fields:[{key:"bio",label:"Biyografi",placeholder:"Kendinizden bahsedin...",multi:true}]}
    ]},
    client:{label:"Danışan Profili",sections:[
      {title:"Kişisel Bilgiler",fields:[{key:"fullName",label:"Ad Soyad",defaultVal:userName},{key:"birthDate",label:"Doğum Tarihi"},{key:"birthPlace",label:"Doğum Yeri"},{key:"education",label:"Eğitim"},{key:"profession",label:"Meslek"},{key:"maritalStatus",label:"Medeni Durum"}]},
      {title:"Sağlık & Klinik",fields:[{key:"diagnosis",label:"Tanı"},{key:"medications",label:"İlaçlar"},{key:"mobility",label:"Hareket Durumu"},{key:"living",label:"Yaşam Düzeni"}]},
      {title:"Acil İletişim",fields:[{key:"eName",label:"Acil Kişi"},{key:"ePhone",label:"Acil Telefon"},{key:"notes",label:"Ek Notlar",multi:true}]}
    ]},
    family:{label:"Aile Üyesi Profili",sections:[
      {title:"Kişisel Bilgiler",fields:[{key:"fullName",label:"Ad Soyad",defaultVal:userName},{key:"relation",label:"Yakınlık"},{key:"phone",label:"Telefon"},{key:"email",label:"E-Posta",type:"email"},{key:"city",label:"Şehir"}]},
      {title:"Danışanla İlişki",fields:[{key:"visit",label:"Ziyaret Sıklığı"},{key:"cohabiting",label:"Birlikte Yaşıyor?"},{key:"careRole",label:"Bakım Rolü"}]},
      {title:"Paylaşmak İstediklerim",fields:[{key:"memories",label:"Aktarmak İstediğim Anılar",multi:true},{key:"concerns",label:"Endişelerimi Terapiste İlet",multi:true}]}
    ]}
  };
  const cfg=FIELDS[role]||FIELDS.therapist;
  const [form,setForm]=useState<any>(()=>{ const r:any={}; cfg.sections.forEach((s:any)=>s.fields.forEach((f:any)=>{r[f.key]=f.defaultVal||"";})); return r; });
  const handlePhoto=(e:any)=>{ const file=e.target.files[0]; if(!file)return; const url=URL.createObjectURL(file); setPhoto(url); onPhotoChange?.(url); };
  const save=()=>{ setEditing(false); setSaved(true); setTimeout(()=>setSaved(false),2500); };
  const inpStyle=(dis:boolean):any=>({ width:"100%",border:`1.5px solid ${dis?T.border:T.borderD}`,borderRadius:9,padding:"9px 12px",fontSize:13,color:T.text,background:dis?T.surfaceAlt:T.surface,fontFamily:"'DM Sans',system-ui,sans-serif" });
  return (
    <div className="fade-up">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10 }}>
        <SectionTitle sub="Bilgilerinizi güncelleyin · Terapist görüntüleyebilir">{cfg.label}</SectionTitle>
        <div style={{ display:"flex",gap:8 }}>
          {saved&&<Badge color={T.green}>✓ Kaydedildi</Badge>}
          {editing?<Btn onClick={save}>Kaydet</Btn>:<Btn variant="ghost" onClick={()=>setEditing(true)}>Düzenle</Btn>}
        </div>
      </div>
      <Card style={{ display:"flex",gap:20,alignItems:"flex-start",marginBottom:14,flexWrap:"wrap" }}>
        <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:8,flexShrink:0 }}>
          <div style={{ width:88,height:88,borderRadius:"50%",background:accent+"15",border:`3px solid ${accent}40`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center" }}>
            {photo?<img src={photo} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>:<span style={{ fontSize:36 }}>{role==="therapist"?"👨‍⚕️":role==="client"?"🧓":"👨‍👩‍👧"}</span>}
          </div>
          <Btn size="sm" variant="outline" onClick={()=>photoRef.current.click()}>Fotoğraf</Btn>
          <input ref={photoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handlePhoto}/>
        </div>
        <div style={{ flex:1,minWidth:200 }}>
          <div style={{ fontSize:17,fontWeight:600,marginBottom:4 }}>{form.fullName||userName}</div>
          <div style={{ fontSize:13,color:accent,marginBottom:8 }}>{role==="therapist"?`${form.title||""} · ${form.institution||""}`:role==="client"?form.diagnosis||"Tanı belirtilmedi":`${form.relation||"Aile Üyesi"} · ${form.careRole||""}`}</div>
          {form.bio&&<div style={{ fontSize:12,color:T.muted,fontStyle:"italic",lineHeight:1.7 }}>"{form.bio}"</div>}
        </div>
      </Card>
      {cfg.sections.map((section:any,si:number)=>(
        <Card noPad key={si} style={{ marginBottom:12,overflow:"hidden" }}>
          <div style={{ padding:"10px 18px",borderBottom:`1px solid ${T.border}`,background:T.surfaceAlt,fontSize:13,fontWeight:600,color:T.textSub }}>{section.title}</div>
          <div style={{ padding:"14px 18px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
            {section.fields.map((field:any,fi:number)=>(
              <div key={fi} style={{ gridColumn:field.multi?"1 / -1":"auto" }}>
                <Label>{field.label}</Label>
                {field.multi
                  ?<textarea disabled={!editing} value={form[field.key]} onChange={(e:any)=>setForm((x:any)=>({...x,[field.key]:e.target.value}))} placeholder={!editing?"—":field.placeholder||""} style={{ ...inpStyle(!editing),minHeight:68,resize:"vertical",marginTop:4 }}/>
                  :<input disabled={!editing} type={field.type||"text"} value={form[field.key]} onChange={(e:any)=>setForm((x:any)=>({...x,[field.key]:e.target.value}))} placeholder={!editing?"—":field.placeholder||""} style={{ ...inpStyle(!editing),marginTop:4 }}/>}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Card noPad style={{ marginBottom:14,overflow:"hidden" }}>
        <div style={{ padding:"10px 18px",borderBottom:`1px solid ${T.border}`,background:T.surfaceAlt,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <span style={{ fontSize:13,fontWeight:600,color:T.textSub }}>Belgeler</span>
          <Btn size="sm" variant="outline" onClick={()=>docRef.current.click()}>+ Ekle</Btn>
        </div>
        <input ref={docRef} type="file" style={{ display:"none" }} onChange={(e:any)=>{ const f=e.target.files[0]; if(f)setDocs((d:any)=>[...d,{name:f.name,size:(f.size/1024).toFixed(1)+"KB",date:new Date().toLocaleDateString("tr-TR")}]); }} accept=".pdf,.jpg,.png,.doc,.docx"/>
        <div style={{ padding:"12px 18px" }}>
          {docs.length===0?<div onClick={()=>docRef.current.click()} style={{ border:`2px dashed ${accent}30`,borderRadius:10,padding:16,textAlign:"center",cursor:"pointer",background:accent+"05" }}><div style={{ fontSize:12,color:accent,fontWeight:500 }}>PDF, görsel veya Word yükleyin</div><div style={{ fontSize:11,color:T.mutedL,marginTop:4 }}>KVKK korumalı</div></div>
          :docs.map((d:any,i:number)=>(
            <div key={i} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<docs.length-1?`1px solid ${T.border}`:"none" }}>
              <div style={{ width:32,height:32,borderRadius:8,background:accent+"15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14 }}>📄</div>
              <div style={{ flex:1,minWidth:0 }}><div style={{ fontWeight:600,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{d.name}</div><div style={{ fontSize:11,color:T.muted }}>{d.date}</div></div>
              <Badge color={T.green} small>Yüklendi</Badge>
            </div>
          ))}
        </div>
      </Card>
      <div style={{ background:T.greenL,border:`1px solid ${T.green}30`,borderRadius:10,padding:12,display:"flex",gap:10 }}>
        <StatusDot color={T.green}/>
        <div style={{ fontSize:12,color:T.textSub,lineHeight:1.7 }}><strong>Gizlilik:</strong> Profil bilgileriniz KVKK ve GDPR kapsamında şifreli olarak saklanır.</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────
const E_TRJ = [{s:"S1",d:48,u:42,a:35},{s:"S2",d:52,u:45,a:40},{s:"S3",d:55,u:50,a:48},{s:"S4",d:61,u:57,a:55},{s:"S5",d:65,u:62,a:60},{s:"S6",d:68,u:66,a:65},{s:"S7",d:72,u:68,a:72}];
const PROTOCOLS:any = {
  bilişsel:{label:"Bilişsel İşlev",icon:"🧠",color:T.primary,ref:"Pérez-Sáez vd. (2021)",format:"Bireysel · 13-26 seans · 45-50 dk",tools:["MMSE","MoCA","ADAS-Cog","AMT"],themes:[{t:"Çocukluk",tip:"En erken hatırladığınız mevsim?"},{t:"Okul Yılları",tip:"Bir öğretmeninizi hatırlıyor musunuz?"},{t:"Aile ve Ev",tip:"Büyüdüğünüz evin en özel köşesi?"},{t:"İş Hayatı",tip:"En gurur duyduğunuz başarınız?"},{t:"Dönüm Noktaları",tip:"Hayatınızı en çok değiştiren karar?"},{t:"Bilgelik",tip:"Gençlere en önemli tavsiyeniz?"}]},
  depresyon:{label:"Depresyon & Ruh Hali",icon:"🌤️",color:T.green,ref:"Wu vd. (2023) RT-MeST",format:"Grup (4-7 kişi) · 4-6 hafta · 90 dk",tools:["GDS","PHQ-9","AMT","Ruminasyon Ölçeği"],themes:[{t:"Olumlu Anılar",tip:"Kendinizi güçlü hissettiğiniz an?"},{t:"Başarı",tip:"Kendinizle gurur duyduğunuz an?"},{t:"Sevgi",tip:"Sevgiyi en güçlü hissettiğiniz an?"},{t:"Başa Çıkma",tip:"Zor dönemde sizi destekleyen ne?"},{t:"Anlam",tip:"Hayatınıza anlam katan nedir?"},{t:"Umut",tip:"İleride görmek istediğiniz?"}]},
  yalnızlık:{label:"Yalnızlık & Bağlılık",icon:"🤝",color:T.family,ref:"Gaggioli vd. (2014)",format:"Grup (6 kişi) · 6-12 hafta · 60 dk",tools:["UCLA Yalnızlık","GDS","Yaşam Doyumu"],themes:[{t:"Arkadaşlıklar",tip:"En yakın çocukluk arkadaşınız?"},{t:"Aidiyet",tip:"En çok ait hissettiğiniz yer?"},{t:"Kutlamalar",tip:"En güzel kutladığınız gün?"},{t:"Yardımlaşma",tip:"Birine yardım ettiğinizde?"},{t:"Aile",tip:"Aileden ilk güzel anı?"},{t:"Paylaşım",tip:"En çok keyif aldığınız paylaşım?"}]},
  yaşamKalitesi:{label:"Yaşam Kalitesi",icon:"🌿",color:T.amber,ref:"Sales vd. (2022)",format:"Karma · 6-12 hafta · 2×90 dk",tools:["QoL-AD","PIL","SOC-13","COPE-28"],themes:[{t:"Yaşam Hikayesi",tip:"Kendinizi bir cümleyle tanıtın."},{t:"Kimlik",tip:"Hiç değişmeyen bir değeriniz?"},{t:"Kararlar",tip:"En cesur kararınız?"},{t:"Dayanıklılık",tip:"Sizi ayakta tutan ne?"},{t:"Anlam",tip:"Hayata tutunmanızı sağlayan?"},{t:"Miras",tip:"Bırakmak istediğiniz şey?"}]},
};

// ─────────────────────────────────────────────────────────────
// TERAPİST PANELİ
// ─────────────────────────────────────────────────────────────
const ProDash = ({ user, onLogout, meetCode, onMeetCode }:any) => {
  const [view,setView]=useState("dashboard");
  const [selC,setSelC]=useState<any>(null);
  const [ph,setPh]=useState(0);
  const [notes,setNotes]=useState(""); const [ci,setCi]=useState(5); const [narr,setNarr]=useState("");
  const [profilePhoto,setProfilePhoto]=useState<string|null>(null);
  const [sidebarOpen,setSidebarOpen]=useState(false);

  // ── Supabase veri state'leri ──
  const [clients,setClients]=useState<any[]>([]);
  const [mems,setMems]=useState<any[]>([]);
  const [contributions,setContributions]=useState<any[]>([]);
  const [loadingClients,setLoadingClients]=useState(true);

  // ── Veri yükleme ──
  useEffect(()=>{
    fetchClients(user?.id).then(data=>{ setClients(data); setLoadingClients(false); });
  },[user?.id]);

  useEffect(()=>{
    if(!selC) return;
    fetchMemories(selC.id).then(setMems);
    fetchContributions(selC.id).then(setContributions);
  },[selC]);

  // İlk danışanı otomatik seç (dashboard için özet)
  useEffect(()=>{ if(clients.length && !selC) setSelC(clients[0]); },[clients]);

  const newBadgeCount = contributions.filter(c=>!c.is_read).length;
  const [showMod,setShowMod]=useState(false); const [modAge,setModAge]=useState("0–10");
  const [selProto,setSelProto]=useState("bilişsel"); const [selTheme,setSelTheme]=useState(0);
  const proto = PROTOCOLS[selProto];
  const sPs=[
    {p:"Duygusal Giriş",icon:"💭",d:proto.themes[selTheme]?.tip||"Bugün nasıl hissediyorsunuz?",color:T.green,subs:["Ruh hali (0-10)","Beden farkındalığı","Odaklanma"]},
    {p:"Bellek Aktivasyonu",icon:"🕰️",d:`Tema: ${proto.themes[selTheme]?.t} — materyal ile uyarım`,color:T.primary,subs:["Materyal gösterimi","Serbest çağrışım","Açık soru"]},
    {p:"Anlatı Yazımı",icon:"✍️",d:"Otobiyografik anlatı akışını yapılandırın",color:T.amber,subs:["Kronolojik anlatı","Duygu etiketleme","Aile bağlamı"]},
    {p:"Yansıtıcı Sorgulama",icon:"🔍",d:"Entegratif yeniden değerlendirme · başa çıkma",color:T.family,subs:["Yeniden değerlendirme","Ruminasyon kontrolü","Kimlik bütünleşme"]},
    {p:"Anlam Bütünleştirme",icon:"🌿",d:"Yaşam hikayesinde bu anının yeri",color:"#DB7093",subs:["Ego bütünlüğü","Sonraki seans köprüsü","Son ruh hali"]},
  ];
  const nav=[
    {id:"dashboard",label:"Terapist Paneli",icon:"⊞"},
    {id:"meet",label:"Görüşme Merkezi",icon:"⊡"},
    {id:"family-hub",label:"Aile Katkıları",icon:"◎",badge:true},
    {id:"timeline",label:"Yaşam Çizelgesi",icon:"◷"},
    {id:"session",label:"Seans Akışı",icon:"◈"},
    {id:"research",label:"Araştırma Modu",icon:"◉"},
    {id:"profile",label:"Profilim",icon:"◯"},
  ];
  const ags=["0–10","10–20","20–30","30–40","40–50","50–60","60–70","70–80"];
  const navTo=(id:string)=>{setView(id);setSelC(null);setSidebarOpen(false);};
  return (
    <div className="dash-layout" style={{ background:T.bg,fontFamily:"'DM Sans',system-ui,sans-serif",color:T.text }}>
      <GlobalStyle/>
      {showMod&&<MemModal onSave={(m:any)=>setMems(ms=>[...ms,m])} onClose={()=>setShowMod(false)} ageGroup={modAge}/>}
      <Sidebar nav={nav} active={view} onNav={navTo} user={user} role="therapist" photo={profilePhoto} onLogout={onLogout} meetActive={!!meetCode} badge={4} open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <div className="dash-content">
        <TopBar title={nav.find(n=>n.id===view)?.label||"Terapist Paneli"} sub={new Date().toLocaleDateString("tr-TR",{weekday:"long",year:"numeric",month:"long",day:"numeric"})} meetCode={meetCode} onHamburger={()=>setSidebarOpen(true)}/>
        <div className="dash-pad fade-up" style={{ padding:20 }}>

          {/* MEET */}
          {view==="meet"&&<MeetingCenter role="therapist" sharedCode={meetCode} onCodeSet={onMeetCode} userCode={user?.email}/>}

          {/* DASHBOARD */}
          {view==="dashboard"&&(
            <div>
              <div style={{ background:`linear-gradient(135deg,${T.primaryL},#EFF7EC)`,border:`1px solid ${T.primary}20`,borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",gap:12,alignItems:"center" }}>
                <div style={{ width:34,height:34,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14,flexShrink:0 }}>✦</div>
                <div>
                  <div style={{ fontSize:11,color:T.muted,marginTop:2 }}>KVKK · Uçtan uca şifreleme · Helsinki Deklerasyonu uyumlu</div>
                </div>
              </div>
              <div className="grid-4" style={{ marginBottom:16 }}>
                {[{label:"Aktif Danışan",value:"3",color:T.primary},{label:"Bu Ay Seans",value:"6",color:T.green},{label:"Aile Katkısı",value:"6",color:T.family},{label:"Risk İzleme",value:"1",color:T.amber}].map((s,i)=>(
                  <Card key={i}><div style={{ fontSize:10,color:T.muted,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",marginBottom:6 }}>{s.label}</div><div style={{ fontSize:28,fontWeight:600,color:s.color }}>{s.value}</div></Card>
                ))}
              </div>
              {/* Client table — scrollable on mobile */}
              <Card noPad style={{ marginBottom:16,overflow:"hidden" }}>
                <div style={{ padding:"12px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                  <span style={{ fontWeight:600,fontSize:14 }}>Danışan Listesi</span>
                  <div style={{ display:"flex",gap:8 }}>
                    <Badge color={T.green} small>ETİK ONAYLI</Badge>
                    <Btn size="sm" variant="outline" onClick={()=>{}}>+ Danışan Ekle</Btn>
                  </div>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:620 }}>
                    <thead><tr style={{ background:T.surfaceAlt }}>
                      {["Danışan","Yaş","Seans","LCI","Danışan Kodu","Risk",""].map((h,i)=>(
                        <th key={i} style={{ padding:"8px 14px",textAlign:"left",color:T.muted,fontWeight:600,fontSize:11,letterSpacing:.5,borderBottom:`1px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {loadingClients
                        ? <tr><td colSpan={7} style={{ padding:"20px",textAlign:"center",color:T.muted }}>Yükleniyor...</td></tr>
                        : clients.map((c,i)=>(
                        <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}`,background:i%2===0?T.surface:T.surfaceAlt }}>
                          <td style={{ padding:"10px 14px",fontWeight:600 }}>{c.name}</td>
                          <td style={{ padding:"10px 14px",color:T.muted }}>{c.age}</td>
                          <td style={{ padding:"10px 14px" }}>{c.sessions}</td>
                          <td style={{ padding:"10px 14px" }}>
                            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                              <div style={{ width:36,height:4,background:T.border,borderRadius:4 }}><div style={{ width:`${c.lci}%`,height:"100%",background:c.lci>70?T.green:T.amber,borderRadius:4 }}/></div>
                              <span style={{ fontSize:11,color:T.muted }}>{c.lci}</span>
                            </div>
                          </td>
                          <td style={{ padding:"10px 14px" }}>
                            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                              <span style={{ fontFamily:"'DM Mono',monospace",fontSize:11,background:T.primaryL,color:T.primaryD,padding:"2px 8px",borderRadius:6,letterSpacing:.5 }}>{c.code}</span>
                              <button onClick={()=>navigator.clipboard?.writeText(c.code).catch(()=>{})} style={{ background:"none",border:"none",cursor:"pointer",color:T.mutedL,fontSize:11,padding:0 }} title="Kopyala">⎘</button>
                            </div>
                          </td>
                          <td style={{ padding:"10px 14px" }}><Badge small color={c.risk==="düşük"?T.green:T.amber}>{c.risk}</Badge></td>
                          <td style={{ padding:"10px 14px" }}><Btn size="sm" variant="outline" onClick={()=>{setSelC(c);setView("client");}}>Profil</Btn></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              {/* Schedule */}
              <Card>
                <div style={{ fontWeight:600,fontSize:14,marginBottom:14 }}>Yaklaşan Seanslar</div>
                {clients.filter(c=>c.next_session).slice(0,4).map((c,i,arr)=>(
                  <div key={c.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<arr.length-1?`1px solid ${T.border}`:"none",flexWrap:"wrap" }}>
                    <div style={{ background:T.sidebar,color:"rgba(255,255,255,.8)",borderRadius:8,padding:"5px 10px",fontSize:12,fontWeight:600,flexShrink:0 }}>{new Date(c.next_session).toLocaleDateString("tr-TR",{day:"2-digit",month:"short"})}</div>
                    <div style={{ flex:1,minWidth:120 }}><div style={{ fontWeight:600,fontSize:13 }}>{c.name}</div><div style={{ fontSize:11,color:T.muted }}>{c.sessions}. seans</div></div>
                    <Btn size="sm" variant="outline" onClick={()=>setView("meet")}>Görüşme Kur</Btn>
                  </div>
                ))}
                {clients.filter(c=>c.next_session).length===0 && (
                  <div style={{ fontSize:13,color:T.muted,padding:"10px 0" }}>Planlanmış seans bulunmuyor.</div>
                )}
              </Card>
            </div>
          )}

          {/* CLIENT DETAIL */}
          {view==="client"&&selC&&(
            <div>
              <button onClick={()=>setView("dashboard")} style={{ background:"none",border:"none",color:T.muted,fontSize:13,fontWeight:500,cursor:"pointer",marginBottom:14,padding:0,display:"flex",alignItems:"center",gap:4 }}>← Panele Dön</button>
              <Card style={{ display:"flex",alignItems:"center",gap:14,marginBottom:16,flexWrap:"wrap" }}>
                <div style={{ width:44,height:44,borderRadius:"50%",background:T.primaryL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20 }}>👤</div>
                <div style={{ flex:1 }}><h2 style={{ margin:0,fontSize:17,fontWeight:600 }}>{selC.name}</h2><p style={{ margin:"2px 0 0",color:T.muted,fontSize:12 }}>{selC.age} yaşında · {selC.sessions} seans</p></div>
                <Badge small color={selC.risk==="düşük"?T.green:T.amber}>Risk: {selC.risk}</Badge>
              </Card>
              {/* Erişim kodları */}
              <div className="grid-2" style={{ marginBottom:16 }}>
                <Card>
                  <div style={{ fontSize:10,color:T.muted,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",marginBottom:8 }}>Danışan Giriş Kodu</div>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    <span style={{ fontFamily:"'DM Mono',monospace",fontSize:15,fontWeight:600,color:T.primaryD,letterSpacing:1.5 }}>{selC.code}</span>
                    <button onClick={()=>navigator.clipboard?.writeText(selC.code).catch(()=>{})} style={{ background:T.primaryL,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11,color:T.primary,fontWeight:600 }}>Kopyala</button>
                  </div>
                  <div style={{ fontSize:11,color:T.muted,marginTop:6 }}>Danışana iletilir · Giriş ekranında kullanılır</div>
                </Card>
                <Card>
                  <div style={{ fontSize:10,color:T.muted,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",marginBottom:8 }}>Aile Erişim Kodu</div>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    <span style={{ fontFamily:"'DM Mono',monospace",fontSize:15,fontWeight:600,color:T.family,letterSpacing:1.5 }}>{selC.famCode}</span>
                    <button onClick={()=>navigator.clipboard?.writeText(selC.famCode).catch(()=>{})} style={{ background:T.family+"15",border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11,color:T.family,fontWeight:600 }}>Kopyala</button>
                  </div>
                  <div style={{ fontSize:11,color:T.muted,marginTop:6 }}>Aile üyelerine iletilir · Farklı oturum</div>
                </Card>
              </div>
              <div className="grid-3">
                {[{label:"LCI",value:selC.lci,desc:"Yaşam Bütünleşme"},{label:"EIS",value:selC.eis,desc:"Duygusal Uyum"},{label:"TRM",value:selC.trm,desc:"Travma Çerçeveleme"}].map((m,i)=>(
                  <Card key={i}><div style={{ fontSize:10,color:T.muted,fontWeight:600,letterSpacing:.8,textTransform:"uppercase",marginBottom:6 }}>{m.desc}</div><div style={{ fontSize:26,fontWeight:600,color:m.value>70?T.green:T.amber,marginBottom:6 }}>{m.value}<span style={{ fontSize:12,color:T.mutedL }}>/100</span></div><div style={{ height:4,background:T.border,borderRadius:4 }}><div style={{ width:`${m.value}%`,height:"100%",background:m.value>70?T.green:T.amber,borderRadius:4 }}/></div></Card>
                ))}
              </div>
            </div>
          )}

          {/* FAMILY HUB */}
          {view==="family-hub"&&(
            <div>
              <SectionTitle sub="Aile üyelerinden gelen katkılar">Aile Katkı Merkezi</SectionTitle>
              {[{from:"Fatma Yıldırım (Kızı)",label:"Sesli mesaj",date:"18 Şub",color:T.primary},{from:"Ali Yıldırım (Oğlu)",label:"Fotoğraf — Eski ev",date:"17 Şub",color:T.green},{from:"Fatma Yıldırım",label:"Anı hikayesi",date:"15 Şub",color:T.family}].map((m,i)=>(
                <Card key={i} style={{ marginBottom:10,borderLeft:`3px solid ${m.color}` }}>
                  <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
                    <div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:13 }}>{m.label}</div><div style={{ fontSize:11,color:T.muted,marginTop:2 }}>{m.from} · {m.date}</div></div>
                    <Btn size="sm" variant="outline">İncele</Btn>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* TIMELINE */}
          {view==="timeline"&&(
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:10 }}>
                <SectionTitle sub="Danışanın otobiyografik bellek haritası">Yaşam Çizelgesi</SectionTitle>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  {ags.map(a=><Chip key={a} active={false} color={T.primary} onClick={()=>{setModAge(a);setShowMod(true);}}>{a}</Chip>)}
                </div>
              </div>
              <div className="grid-3">
                {mems.map(m=>(
                  <Card key={m.id} style={{ borderTop:`3px solid ${m.valence>=0?T.green:T.danger}` }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                      <span style={{ fontSize:10,color:T.muted,fontWeight:500,background:T.surfaceAlt,padding:"2px 8px",borderRadius:6 }}>{m.age}{m.date?" · "+m.date:""}</span>
                      <span style={{ fontWeight:700,fontSize:12,color:m.valence>=0?T.green:T.danger }}>{m.valence>0?"+":""}{m.valence}</span>
                    </div>
                    <div style={{ fontWeight:600,fontSize:13,marginBottom:6 }}>{m.title}</div>
                    <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
                      {m.tags.map((t:string)=><span key={t} style={{ fontSize:10,background:T.tag[t as keyof typeof T.tag]+"18",color:T.tag[t as keyof typeof T.tag],border:`1px solid ${T.tag[t as keyof typeof T.tag]}30`,padding:"2px 8px",borderRadius:20,fontWeight:600 }}>{t}</span>)}
                    </div>
                  </Card>
                ))}
                <Card style={{ border:`2px dashed ${T.border}`,background:T.surfaceAlt,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",minHeight:110 }} onClick={()=>setShowMod(true)}>
                  <div style={{ fontSize:22,color:T.primary }}>+</div>
                  <div style={{ fontWeight:600,color:T.muted,fontSize:13,marginTop:4 }}>Yeni Anı Ekle</div>
                </Card>
              </div>
            </div>
          )}

          {/* SESSION */}
          {view==="session"&&(
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:10 }}>
                <SectionTitle sub="Kanıta dayalı protokol — 162 çalışma meta-analizi">Seans Akışı</SectionTitle>
                {meetCode?<Btn onClick={()=>(()=>{ const u='https://meet.google.com/'+meetCode; const w=Math.min(1100,window.screen.availWidth-100); const h=Math.min(750,window.screen.availHeight-100); window.open(u,'google_meet_animsama','width='+w+',height='+h+',left='+Math.round((window.screen.availWidth-w)/2)+',top='+Math.round((window.screen.availHeight-h)/2)+',resizable=yes'); })()}>Meet'i Aç</Btn>:<Btn variant="outline" onClick={()=>setView("meet")}>Görüşme Kur</Btn>}
              </div>
              {/* Protocol */}
              <Card style={{ marginBottom:12 }}>
                <div style={{ fontWeight:600,fontSize:13,marginBottom:10,color:T.textSub }}>Protokol Seç</div>
                <div className="grid-proto" style={{ marginBottom:10 }}>
                  {Object.entries(PROTOCOLS).map(([k,pr]:any)=>(
                    <button key={k} onClick={()=>{setSelProto(k);setSelTheme(0);setPh(0);}} style={{ padding:"9px 6px",borderRadius:10,border:`1.5px solid ${selProto===k?pr.color:T.border}`,background:selProto===k?pr.color+"12":T.surfaceAlt,cursor:"pointer",textAlign:"center",transition:"all .18s",fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                      <div style={{ fontSize:17,marginBottom:2 }}>{pr.icon}</div>
                      <div style={{ fontWeight:600,fontSize:10,color:selProto===k?pr.color:T.muted,lineHeight:1.4 }}>{pr.label}</div>
                    </button>
                  ))}
                </div>
                <div style={{ background:T.surfaceAlt,borderRadius:8,padding:"8px 12px",fontSize:11,color:T.muted }}>
                  <span style={{ fontWeight:600,color:proto.color }}>Kaynak: </span>{proto.ref} <span style={{ marginLeft:10,fontWeight:600 }}>Format: </span>{proto.format}
                </div>
              </Card>
              {/* Themes */}
              <Card style={{ marginBottom:12 }}>
                <div style={{ fontWeight:600,fontSize:13,marginBottom:10,color:T.textSub }}>Tema</div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  {proto.themes.map((th:any,i:number)=><Chip key={i} active={selTheme===i} color={proto.color} onClick={()=>{setSelTheme(i);setPh(0);}}>{th.t}</Chip>)}
                </div>
                {proto.themes[selTheme]&&<div style={{ marginTop:10,background:proto.color+"10",borderLeft:`3px solid ${proto.color}`,borderRadius:"0 8px 8px 0",padding:"8px 12px",fontSize:12,color:proto.color,fontWeight:600 }}>"{proto.themes[selTheme].tip}"</div>}
              </Card>
              {/* Phase tabs */}
              <div className="grid-phases" style={{ marginBottom:12,background:T.surface,borderRadius:12,padding:5,border:`1px solid ${T.border}` }}>
                {sPs.map((s:any,i:number)=>(
                  <button key={i} onClick={()=>setPh(i)} style={{ padding:"8px 4px",borderRadius:8,border:"none",cursor:"pointer",background:ph===i?s.color:T.surfaceAlt,color:ph===i?"#fff":T.muted,fontWeight:ph===i?600:400,fontSize:10,display:"flex",flexDirection:"column",alignItems:"center",gap:2,transition:"all .18s",fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                    <span style={{ fontSize:14 }}>{s.icon}</span>
                    <span style={{ textAlign:"center",lineHeight:1.3 }}>{s.p}</span>
                  </button>
                ))}
              </div>
              {/* Active phase */}
              <Card style={{ border:`1.5px solid ${sPs[ph].color}25`,marginBottom:12 }}>
                <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:12 }}>
                  <span style={{ fontSize:20 }}>{sPs[ph].icon}</span>
                  <div><div style={{ fontWeight:600,fontSize:14 }}>{sPs[ph].p}</div><div style={{ fontSize:11,color:T.muted }}>Aşama {ph+1}/5</div></div>
                </div>
                <div style={{ background:T.surfaceAlt,borderLeft:`3px solid ${sPs[ph].color}`,borderRadius:"0 8px 8px 0",padding:"9px 12px",fontSize:13,lineHeight:1.8,color:T.textSub,fontStyle:"italic",marginBottom:12 }}>"{sPs[ph].d}"</div>
                <div style={{ display:"flex",gap:5,flexWrap:"wrap",marginBottom:12 }}>
                  {sPs[ph].subs.map((sub:string,i:number)=><span key={i} style={{ fontSize:11,background:sPs[ph].color+"12",color:sPs[ph].color,border:`1px solid ${sPs[ph].color}25`,padding:"3px 10px",borderRadius:16,fontWeight:600 }}>✓ {sub}</span>)}
                </div>
                {ph===0&&(<div><div style={{ fontSize:11,color:T.muted,fontWeight:600,marginBottom:5 }}>Başlangıç Ruh Hali (0–10)</div><input type="range" min={0} max={10} value={ci} onChange={(e:any)=>setCi(+e.target.value)} style={{ width:"100%",accentColor:sPs[ph].color }}/><div style={{ display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,marginTop:3 }}><span>0</span><span style={{ fontWeight:700,color:sPs[ph].color }}>{ci}/10</span><span>10</span></div></div>)}
                {ph===2&&<Txta value={narr} onChange={(e:any)=>setNarr(e.target.value)} placeholder="Danışan anlatımını yazın..." style={{ minHeight:80 }}/>}
              </Card>
              <Card style={{ marginBottom:12 }}><div style={{ fontWeight:600,fontSize:13,marginBottom:8,color:T.textSub }}>Klinik Notlar</div><Txta value={notes} onChange={(e:any)=>setNotes(e.target.value)} placeholder="Klinik gözlemler, ruminasyon izleme..." style={{ minHeight:70 }}/></Card>
              <div style={{ display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap" }}>
                <Btn variant="ghost" onClick={()=>setPh(Math.max(0,ph-1))} disabled={ph===0}>← Önceki</Btn>
                <Btn onClick={()=>setPh(Math.min(4,ph+1))} disabled={ph===4} style={{ background:proto.color }}>Sonraki Aşama →</Btn>
              </div>
            </div>
          )}

          {/* RESEARCH */}
          {view==="research"&&(
            <div>
              <SectionTitle sub="Anonim dışa aktarma · KVKK · k-anonimlik ≥ 5">Araştırma Modu</SectionTitle>
              <div style={{ background:T.dangerL,border:`1px solid ${T.danger}25`,borderRadius:10,padding:12,marginBottom:14,display:"flex",gap:10 }}>
                <StatusDot color={T.danger}/><div><div style={{ fontWeight:600,fontSize:12,color:T.danger }}>Gizlilik Koruması Aktif</div><div style={{ fontSize:12,color:T.muted }}>k-anonimlik ≥ 5 zorunludur.</div></div>
              </div>
              <div className="grid-1-1" style={{ marginBottom:14 }}>
                <Card><div style={{ fontWeight:600,fontSize:13,marginBottom:12,color:T.textSub }}>Grup Yörüngesi</div><ResponsiveContainer width="100%" height={150}><LineChart data={E_TRJ}><CartesianGrid strokeDasharray="3 3" stroke={T.border}/><XAxis dataKey="s" tick={{fontSize:10,fill:T.muted}}/><YAxis tick={{fontSize:10,fill:T.muted}} domain={[30,90]}/><Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:12}}/><Line type="monotone" dataKey="d" stroke={T.green} strokeWidth={2} name="Denge" dot={false}/><Line type="monotone" dataKey="u" stroke={T.primary} strokeWidth={2} name="Uyum" dot={false}/><Line type="monotone" dataKey="a" stroke={T.amber} strokeWidth={2} name="Anlam" dot={false}/></LineChart></ResponsiveContainer></Card>
                <Card><div style={{ fontWeight:600,fontSize:13,marginBottom:12,color:T.textSub }}>Ölçüm Güvenilirliği</div>{[{l:"LCI",a:78,c:T.green},{l:"EIS",a:71,c:T.primary},{l:"TRM",a:65,c:T.amber}].map((m,i)=>(<div key={i} style={{ marginBottom:10 }}><div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}><span style={{ fontSize:12,fontWeight:600 }}>{m.l}</span><span style={{ fontSize:11,color:T.muted,fontFamily:"'DM Mono',monospace" }}>α = 0.{m.a}</span></div><div style={{ background:T.border,borderRadius:4,height:5 }}><div style={{ width:`${m.a}%`,height:"100%",background:m.c,borderRadius:4 }}/></div></div>))}<div style={{ fontSize:11,color:T.muted }}>⚠ Validasyon sürmektedir.</div></Card>
              </div>
            </div>
          )}

          {view==="profile"&&<ProfilePanel role="therapist" userName={user.name} accent={T.green} onPhotoChange={setProfilePhoto} currentPhoto={profilePhoto}/>}
        </div>
        <TrustBar/>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DANIŞAN PANELİ
// ─────────────────────────────────────────────────────────────
const ClientDash = ({ user, onLogout, meetCode }:any) => {
  const [tab,setTab]=useState("home"); const [mood,setMood]=useState<number|null>(null); const [diary,setDiary]=useState("");
  const [entries,setEntries]=useState<any[]>([]);
  const [clientData,setClientData]=useState<any>(null);
  const [memories,setMemories]=useState<any[]>([]);
  const [familyMsgs,setFamilyMsgs]=useState<any[]>([]);
  const [savingDiary,setSavingDiary]=useState(false);

  useEffect(()=>{
    // Danışan kodundan profil yükle
    if(user?.code){
      import('./supabase').then(({ fetchClientByCode, fetchDiary, fetchContributions, fetchMemories })=>{
        fetchClientByCode(user.code).then(c=>{
          setClientData(c);
          if(c){
            fetchDiary(c.id).then(setEntries);
            fetchContributions(c.id).then(setFamilyMsgs);
            fetchMemories(c.id).then(setMemories);
          }
        });
      });
    }
  },[user?.code]);

  const saveDiaryEntry = async () => {
    if(!diary.trim()) return;
    setSavingDiary(true);
    try {
      const clientId = clientData?.id;
      if(!clientId) throw new Error("Danışan bulunamadı");
      const entry = await addDiaryEntry({ client_id: clientId, text: diary, mood: mood||3 });
      setEntries(e=>[entry,...e]);
      setDiary("");
    } catch(e:any) {
      setDiary("");
    } finally { setSavingDiary(false); }
  };

  const unreadFamilyCount = familyMsgs.filter(m=>!m.is_read).length;
  const [profilePhoto,setProfilePhoto]=useState<string|null>(null); const [sidebarOpen,setSidebarOpen]=useState(false);
  const moods=["😔","😕","😐","🙂","😊","😄"]; const moodColors=[T.danger,"#EF6C00",T.amber,"#7EB356",T.green,T.green];
  const nav=[{id:"home",label:"Ana Sayfa",icon:"⊞"},{id:"meet",label:"Görüşme",icon:"⊡"},{id:"family",label:"Aile Mesajları",icon:"◎",badge:true},{id:"timeline",label:"Anı Çizelgem",icon:"◷"},{id:"diary",label:"Günlük",icon:"◈"},{id:"profile",label:"Profilim",icon:"◯"}];
  return (
    <div className="dash-layout" style={{ background:T.bg,fontFamily:"'DM Sans',system-ui,sans-serif",color:T.text }}>
      <GlobalStyle/>
      <Sidebar nav={nav} active={tab} onNav={(id:string)=>{setTab(id);setSidebarOpen(false);}} user={user} role="client" photo={profilePhoto} onLogout={onLogout} meetActive={!!meetCode} badge={2} open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <div className="dash-content">
        <TopBar title={nav.find(n=>n.id===tab)?.label||"Ana Sayfa"} meetCode={meetCode} onHamburger={()=>setSidebarOpen(true)}/>
        <div className="dash-pad fade-up" style={{ padding:20 }}>
          {tab==="profile"&&<ProfilePanel role="client" userName={user.name} accent={T.primary} onPhotoChange={setProfilePhoto} currentPhoto={profilePhoto}/>}
          {tab==="meet"&&<MeetingCenter role="client" sharedCode={meetCode} userCode={user?.code}/>}
          {tab==="home"&&(
            <div>
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontSize:21,fontWeight:600,margin:0 }}>Merhaba, {user.name.split(" ")[0]}</h2>
                <p style={{ color:T.muted,marginTop:4,fontSize:13,lineHeight:1.6 }}>Bugün nasıl hissediyorsunuz?</p>
              </div>
              {meetCode&&<div style={{ background:`linear-gradient(135deg,${T.primaryD},${T.primary})`,borderRadius:14,padding:18,marginBottom:18,color:"#fff",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap" }}><div style={{ fontSize:28 }}>🎥</div><div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:15 }}>Görüşme Hazır</div><div style={{ color:"rgba(255,255,255,.7)",fontSize:12,marginTop:2 }}>Terapistiniz görüşmeyi başlattı.</div></div><button onClick={()=>(()=>{ const u='https://meet.google.com/'+meetCode; const w=Math.min(1100,window.screen.availWidth-100); const h=Math.min(750,window.screen.availHeight-100); window.open(u,'google_meet_animsama','width='+w+',height='+h+',left='+Math.round((window.screen.availWidth-w)/2)+',top='+Math.round((window.screen.availHeight-h)/2)+',resizable=yes'); })()} style={{ background:"rgba(255,255,255,.15)",color:"#fff",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:10,padding:"9px 18px",cursor:"pointer",fontWeight:600,fontSize:13,fontFamily:"inherit",whiteSpace:"nowrap" }}>Katıl →</button></div>}
              <Card style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600,fontSize:13,marginBottom:14,color:T.textSub }}>Duygu Durumunuz</div>
                <div style={{ display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap" }}>
                  {moods.map((m,i)=><button key={i} onClick={()=>setMood(i)} style={{ width:46,height:46,borderRadius:12,fontSize:24,background:mood===i?moodColors[i]+"18":"transparent",border:`2px solid ${mood===i?moodColors[i]:T.border}`,cursor:"pointer",transition:"all .2s" }}>{m}</button>)}
                </div>
                {mood!==null&&<div style={{ textAlign:"center",marginTop:10,fontSize:12,color:T.green,fontWeight:600 }}>✓ Kaydedildi</div>}
              </Card>
              <div className="grid-3">
                {[{label:"Toplam Anı",value:"6",color:T.primary},{label:"Seans",value:"7",color:T.green},{label:"Aile Mesajı",value:"2",color:T.family}].map((s,i)=>(
                  <Card key={i} style={{ textAlign:"center" }}><div style={{ fontSize:26,fontWeight:600,color:s.color }}>{s.value}</div><div style={{ fontSize:11,color:T.muted,fontWeight:600,marginTop:4 }}>{s.label}</div></Card>
                ))}
              </div>
            </div>
          )}
          {tab==="family"&&(
            <div>
              <SectionTitle sub="Aile üyelerinizden gelen mesajlar">Aile Mesajları</SectionTitle>
              {[{from:"Fatma Yıldırım (Kızınız)",label:"Annem hakkında anlatmak istediklerim",date:"18 Şub",color:T.primary,type:"Sesli Mesaj"},{from:"Ali Yıldırım (Oğlunuz)",label:"Memleketteki eski evimizin fotoğrafı",date:"17 Şub",color:T.green,type:"Fotoğraf"},{from:"Fatma Yıldırım",label:"Babam hakkındaki anı",date:"15 Şub",color:T.family,type:"Metin"}].map((m,i)=>(
                <Card key={i} style={{ marginBottom:10,borderLeft:`3px solid ${m.color}` }}>
                  <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                    <div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:13,marginBottom:3 }}>{m.label}</div><div style={{ fontSize:11,color:T.muted }}>{m.from} · {m.date}</div></div>
                    <Badge small color={m.color}>{m.type}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {tab==="timeline"&&(
            <div>
              <SectionTitle sub="Otobiyografik bellek haritanız">Anı Çizelgem</SectionTitle>
              <div className="grid-2">
                {[{age:"0–10",title:"Annemin bahçesi",v:4,tags:["childhood"],date:"1958"},{age:"20–30",title:"Evlilik günü",v:5,tags:["love"],date:"1975"},{age:"20–30",title:"Babamın vefatı",v:-4,tags:["loss"],date:"1974"},{age:"30–40",title:"İlk çocuğum",v:5,tags:["love"],date:"1982"}].map((m,i)=>(
                  <Card key={i} style={{ borderTop:`3px solid ${m.v>=0?T.green:T.danger}` }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}><span style={{ fontSize:10,color:T.muted,fontWeight:500 }}>{m.age} · {m.date}</span><span style={{ fontWeight:700,fontSize:12,color:m.v>=0?T.green:T.danger }}>{m.v>0?"+":""}{m.v}</span></div>
                    <div style={{ fontWeight:600,fontSize:13,marginBottom:6 }}>{m.title}</div>
                    <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>{m.tags.map(t=><span key={t} style={{ fontSize:10,background:T.tag[t as keyof typeof T.tag]+"18",color:T.tag[t as keyof typeof T.tag],border:`1px solid ${T.tag[t as keyof typeof T.tag]}30`,padding:"2px 8px",borderRadius:20,fontWeight:600 }}>{t}</span>)}</div>
                  </Card>
                ))}
              </div>
            </div>
          )}
          {tab==="diary"&&(
            <div>
              <SectionTitle sub="Günlük duygu ve düşüncelerinizi kaydedin">Duygusal Günlük</SectionTitle>
              <Card style={{ marginBottom:14 }}>
                <Txta value={diary} onChange={(e:any)=>setDiary(e.target.value)} placeholder="Bugün ne hissediyorsunuz?" style={{ minHeight:90,marginBottom:10 }}/>
                <Btn onClick={saveDiaryEntry} disabled={savingDiary}>{savingDiary?"Kaydediliyor...":"Kaydet"}</Btn>
              </Card>
              {entries.map((e,i)=>(
                <Card key={i} style={{ marginBottom:10,borderLeft:`3px solid ${T.primary}` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}><span style={{ fontSize:12,color:T.muted,fontWeight:600 }}>{e.date}</span><span style={{ fontSize:18 }}>{moods[e.mood]}</span></div>
                  <div style={{ fontSize:13,lineHeight:1.7 }}>{e.text}</div>
                </Card>
              ))}
            </div>
          )}
        </div>
        <TrustBar/>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AİLE PANELİ
// ─────────────────────────────────────────────────────────────
const FamilyDash = ({ user, onLogout, meetCode }:any) => {
  const [tab,setTab]=useState("home"); const [photos,setPhotos]=useState<any[]>([]); const [stories,setStories]=useState<any[]>([]);
  const [stT,setStT]=useState(""); const [stTx,setStTx]=useState(""); const [stP,setStP]=useState("Çocukluk");
  const [profilePhoto,setProfilePhoto]=useState<string|null>(null); const [sidebarOpen,setSidebarOpen]=useState(false);
  const fileRef=useRef<any>(); const periods=["Çocukluk","Gençlik","Evlilik","Ebeveynlik","Orta Yaş","Emeklilik"];
  const nav=[{id:"home",label:"Ana Sayfa",icon:"⊞"},{id:"meet",label:"Görüşme",icon:"⊡"},{id:"voice",label:"Sesli Mesaj",icon:"◎"},{id:"photos",label:"Fotoğraflar",icon:"◷"},{id:"stories",label:"Anı Hikayeleri",icon:"◈"},{id:"info",label:"Terapiste Bilgi",icon:"◉"},{id:"profile",label:"Profilim",icon:"◯"}];
  return (
    <div className="dash-layout" style={{ background:T.bg,fontFamily:"'DM Sans',system-ui,sans-serif",color:T.text }}>
      <GlobalStyle/>
      <Sidebar nav={nav} active={tab} onNav={(id:string)=>{setTab(id);setSidebarOpen(false);}} user={user} role="family" photo={profilePhoto} onLogout={onLogout} meetActive={!!meetCode} badge={0} open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <div className="dash-content">
        <TopBar title={nav.find(n=>n.id===tab)?.label||"Aile Paneli"} meetCode={meetCode} onHamburger={()=>setSidebarOpen(true)}/>
        <div className="dash-pad fade-up" style={{ padding:20 }}>
          {tab==="profile"&&<ProfilePanel role="family" userName={user.name} accent={T.family} onPhotoChange={setProfilePhoto} currentPhoto={profilePhoto}/>}
          {tab==="meet"&&<MeetingCenter role="family" sharedCode={meetCode} userCode={user?.code}/>}
          {tab==="home"&&(
            <div>
              <div style={{ marginBottom:20 }}><h2 style={{ fontSize:21,fontWeight:600,margin:0 }}>Merhaba, {user.name.split(" ")[0]}</h2><p style={{ color:T.muted,marginTop:4,fontSize:13,lineHeight:1.6 }}>Sevdiğinizin terapi sürecine katkılarınız değerli.</p></div>
              {meetCode&&<div style={{ background:`linear-gradient(135deg,${T.primaryD},${T.primary})`,borderRadius:14,padding:18,marginBottom:18,color:"#fff",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap" }}><div style={{ fontSize:28 }}>🎥</div><div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:15 }}>Görüşme Hazır</div></div><button onClick={()=>(()=>{ const u='https://meet.google.com/'+meetCode; const w=Math.min(1100,window.screen.availWidth-100); const h=Math.min(750,window.screen.availHeight-100); window.open(u,'google_meet_animsama','width='+w+',height='+h+',left='+Math.round((window.screen.availWidth-w)/2)+',top='+Math.round((window.screen.availHeight-h)/2)+',resizable=yes'); })()} style={{ background:"rgba(255,255,255,.15)",color:"#fff",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:10,padding:"9px 18px",cursor:"pointer",fontWeight:600,fontSize:13,fontFamily:"inherit" }}>Katıl →</button></div>}
              <div className="grid-3">
                {[{label:"Sesli Mesaj",desc:"Ses kaydı bırakın",action:()=>setTab("voice")},{label:"Fotoğraf Paylaş",desc:"Aile fotoğrafları",action:()=>setTab("photos")},{label:"Anı Hikayesi",desc:"Anılarını anlatın",action:()=>setTab("stories")}].map((c,i)=>(
                  <Card key={i} onClick={c.action} style={{ cursor:"pointer",textAlign:"center",borderTop:`3px solid ${T.primary}`,transition:"all .2s" }}><div style={{ fontWeight:600,fontSize:13,marginBottom:4 }}>{c.label}</div><div style={{ fontSize:12,color:T.muted }}>{c.desc}</div></Card>
                ))}
              </div>
            </div>
          )}
          {tab==="voice"&&(<div><SectionTitle sub="Kaydedilen mesajlar terapiste iletilir">Sesli Mesaj</SectionTitle><VoiceRecorder onSave={()=>{}}/></div>)}
          {tab==="photos"&&(
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:10 }}>
                <SectionTitle sub="Seans materyali olarak kullanılabilir">Fotoğraflar</SectionTitle>
                <Btn onClick={()=>fileRef.current.click()}>+ Yükle</Btn>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={(e:any)=>[...e.target.files].forEach((f:File)=>{const url=URL.createObjectURL(f);setPhotos(p=>[{id:Date.now()+Math.random(),url,name:f.name,date:new Date().toLocaleDateString("tr-TR")},...p]);})}/>
              {photos.length===0?<div onClick={()=>fileRef.current.click()} style={{ border:`2px dashed ${T.border}`,borderRadius:12,padding:40,textAlign:"center",cursor:"pointer",background:T.surface }}><div style={{ fontSize:12,color:T.muted }}>Fotoğraf yüklemek için tıklayın</div></div>
              :<div className="grid-3">{photos.map((p:any)=>(<Card key={p.id} noPad style={{ overflow:"hidden" }}><img src={p.url} alt={p.name} style={{ width:"100%",height:140,objectFit:"cover",display:"block" }}/><div style={{ padding:"10px 14px" }}><div style={{ fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.name}</div><div style={{ marginTop:6 }}><Badge small color={T.green}>✓ İletildi</Badge></div></div></Card>))}</div>}
            </div>
          )}
          {tab==="stories"&&(
            <div>
              <SectionTitle sub="Anılarınız terapi sürecine katkı sağlar">Anı Hikayeleri</SectionTitle>
              <Card style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600,fontSize:13,marginBottom:12,color:T.textSub }}>Yeni Hikaye</div>
                <div className="grid-2-1" style={{ marginBottom:10 }}>
                  <div><Label>Başlık</Label><Inp value={stT} onChange={(e:any)=>setStT(e.target.value)} placeholder="Başlık..."/></div>
                  <div><Label>Dönem</Label><select value={stP} onChange={(e:any)=>setStP(e.target.value)} style={{ width:"100%",border:`1.5px solid ${T.borderD}`,borderRadius:9,padding:"9px 12px",fontSize:13,background:T.surface,fontFamily:"'DM Sans',system-ui,sans-serif",color:T.text }}>{periods.map(p=><option key={p}>{p}</option>)}</select></div>
                </div>
                <Txta value={stTx} onChange={(e:any)=>setStTx(e.target.value)} placeholder="Anıyı paylaşın..." style={{ minHeight:90,marginBottom:10 }}/>
                <div style={{ display:"flex",justifyContent:"flex-end" }}><Btn onClick={()=>{ if(stTx.trim()&&stT.trim()){setStories(s=>[{id:Date.now(),title:stT,text:stTx,period:stP,date:new Date().toLocaleDateString("tr-TR")},...s]);setStT("");setStTx("");} }}>Gönder</Btn></div>
              </Card>
              {stories.map((s:any)=>(<Card key={s.id} style={{ marginBottom:10,borderLeft:`3px solid ${T.primary}` }}><div style={{ display:"flex",justifyContent:"space-between",marginBottom:6,flexWrap:"wrap",gap:6 }}><div style={{ fontWeight:600,fontSize:13 }}>{s.title}</div><Badge small color={T.primary}>{s.period}</Badge></div><div style={{ fontSize:13,lineHeight:1.8,color:T.textSub,fontStyle:"italic" }}>"{s.text}"</div><div style={{ marginTop:8 }}><Badge small color={T.green}>✓ İletildi</Badge></div></Card>))}
            </div>
          )}
          {tab==="info"&&(
            <div>
              <SectionTitle sub="Bu bilgiler yalnızca terapiste iletilir">Terapiste Bilgi Ver</SectionTitle>
              <div style={{ background:T.amberL,border:`1px solid ${T.amber}30`,borderRadius:10,padding:12,marginBottom:14,display:"flex",gap:10 }}>
                <StatusDot color={T.amber}/><div style={{ fontSize:12,color:T.textSub,lineHeight:1.7 }}>Paylaşımlarınız KVKK kapsamında gizli tutulur.</div>
              </div>
              {[{label:"Endişe verici bir durum?",placeholder:"Son günlerde üzgün görünüyor..."},{label:"Olumlu bir gelişme?",placeholder:"Fotoğraflara bakarken güldü..."},{label:"Başka bir bilgi?",placeholder:"Paylaşmak istediğiniz..."}].map((f,i)=>(<Card key={i} style={{ marginBottom:10 }}><Label>{f.label}</Label><Txta placeholder={f.placeholder} style={{ minHeight:70,marginTop:5 }}/></Card>))}
              <Btn style={{ width:"100%",justifyContent:"center" }}>Terapiste Gönder</Btn>
            </div>
          )}
        </div>
        <TrustBar/>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// GİRİŞ FORMU
// ─────────────────────────────────────────────────────────────
const LOGIN_CONFIGS:any = {
  pro:{ role:"pro", bg:"linear-gradient(145deg,#E2E2E6,#EBEBEB)",accent:T.green,muted:"#6B7A90",title:"#1A2332",tagline:"Klinik Sorumluluğunuzu\nDijital Ortamda Taşıyın",subtitle:"Profesyonel Girişi",desc:"Butler Yaşam İncelemesi ve Erikson teorisine dayalı protokollerle çalışın.",roles:[{val:"therapist",label:"Terapist"},{val:"supervisor",label:"Süpervizör"},{val:"researcher",label:"Araştırmacı"}],fields:[{key:"email",label:"KURUMSAL E-POSTA",placeholder:"ad.soyad@kurum.edu.tr",type:"email"},{key:"pass",label:"ŞİFRE",placeholder:"••••••••",type:"password"}],secNote:"Tüm oturumlar uçtan uca şifrelenmiştir." },
  client:{ role:"client", bg:"linear-gradient(145deg,#E2E2E6,#EBEBEB)",accent:T.primary,muted:"#6B7A90",title:"#1A2332",tagline:"Anılarınız\nBurada Güvende",subtitle:"Danışan Girişi",desc:"Terapistinizin rehberliğinde yaşam hikayenizi keşfedin.",roles:null,fields:[{key:"code",label:"DANIŞAN KODU",placeholder:"ANS-2026-XXXX"}],secNote:"Bilgileriniz yalnızca terapistinizle paylaşılır." },
  family:{ role:"family", bg:"linear-gradient(145deg,#E2E2E6,#EBEBEB)",accent:T.family,muted:"#6B7A90",title:"#1A2332",tagline:"Sevdiklerinizin İyileşme\nYolculuğuna Ortak Olun",subtitle:"Aile Üyesi Girişi",desc:"Sesli mesajlarınız ve fotoğraflarınız terapi sürecine katkı sağlar.",roles:null,fields:[{key:"code",label:"AİLE ERİŞİM KODU",placeholder:"FAM-2026-XXXX"},{key:"name",label:"ADINIZ SOYADINIZ",placeholder:"Ad Soyad"},{key:"rel",label:"YAKINLIK",placeholder:"Kızı / Oğlu / Eşi"}],secNote:"Paylaşımlarınız şifrelenerek iletilir." },
};

const LoginForm = ({ config, onLogin, onBack, onRegister }:any) => {
  const [fields,setFields]=useState<any>({}); const [loading,setLoading]=useState(false); const [err,setErr]=useState(""); const [role,setRole]=useState(config.roles?.[0]?.val||"");
  const handle=async()=>{
    if(!config.fields.every((f:any)=>fields[f.key])){setErr("Lütfen tüm alanları doldurun.");return;}
    setLoading(true); setErr("");
    try {
      if(config.role==="pro"){
        // Terapist: Supabase e-posta + şifre girişi
        if(!supabase) throw new Error("Sistem bağlantısı kurulamadı.");
        const {data,error}=await supabase.auth.signInWithPassword({email:fields.email,password:fields.pass});
        if(error) throw new Error("E-posta veya şifre hatalı.");
        const {data:profile}=await supabase.from("profiles").select("*").eq("id",data.user.id).single();
        onLogin({...data.user, name:profile?.name||fields.email, role:profile?.role||"therapist"});
      } else if(config.role==="client"){
        // Danışan: kod ile giriş
        if(!supabase) throw new Error("Sistem bağlantısı kurulamadı.");
        const {data,error}=await supabase.from("clients").select("*").eq("code",fields.code).single();
        if(error||!data) throw new Error("Danışan kodu bulunamadı.");
        onLogin({...data, name:data.name, role:"client", code:data.code});
      } else {
        // Aile: aile kodu ile giriş
        if(!supabase) throw new Error("Sistem bağlantısı kurulamadı.");
        const {data,error}=await supabase.from("clients").select("*").eq("fam_code",fields.code).single();
        if(error||!data) throw new Error("Aile erişim kodu bulunamadı.");
        onLogin({name:fields.name, role:"family", relation:fields.rel, client_id:data.id});
      }
    } catch(e:any){
      setErr(e.message||"Giriş başarısız.");
    } finally { setLoading(false); }
  };
  const inpStyle={ width:"100%",background:"#FFFFFF",border:`1.5px solid ${T.borderD}`,borderRadius:10,padding:"12px 14px",color:T.text,fontSize:13,outline:"none",boxSizing:"border-box" as const,fontFamily:"'DM Sans',system-ui,sans-serif" };
  return (
    <div className="login-layout" style={{ background:config.bg,fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <GlobalStyle/>
      {/* Left panel — hidden on mobile */}
      <div className="login-left" style={{ display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 48px",borderRight:`1px solid ${T.border}`,background:"rgba(255,255,255,.5)" }}>
        <div style={{ fontSize:22,fontWeight:700,color:T.text,letterSpacing:-.5,marginBottom:28 }}>Anımsam<span style={{ color:config.accent }}>A</span></div>
        <div style={{ fontSize:22,fontWeight:600,color:T.text,lineHeight:1.5,marginBottom:14 }}>{config.tagline.split("\n").map((l:string,i:number)=><span key={i}>{l}{i===0&&<br/>}</span>)}</div>
        <p style={{ fontSize:13,color:T.muted,lineHeight:1.9,marginBottom:28 }}>{config.desc}</p>
        <div style={{ height:1,background:T.border,marginBottom:24 }}/>
        <div style={{ fontSize:10,color:T.mutedL,fontWeight:600,letterSpacing:1,textTransform:"uppercase",marginBottom:12 }}>Güvenlik</div>
        {[`${new Date().toLocaleDateString("tr-TR")} · Güvenli oturum`,"KVKK / GDPR uyumlu platform","Uçtan uca şifreli bağlantı"].map((t,i)=>(
          <div key={i} style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
            <StatusDot color={config.accent}/><span style={{ fontSize:12,color:T.muted }}>{t}</span>
          </div>
        ))}
      </div>
      {/* Right panel — form */}
      <div style={{ display:"flex",flexDirection:"column",justifyContent:"center",padding:"40px 48px",maxWidth:480,margin:"0 auto",width:"100%",background:"rgba(255,255,255,.7)" }}>
        {/* Mobile logo */}
        <div className="show-mobile" style={{ fontSize:22,fontWeight:700,color:T.text,marginBottom:24 }}>Anımsam<span style={{ color:config.accent }}>A</span></div>
        <button onClick={onBack} style={{ background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13,marginBottom:28,textAlign:"left",padding:0,display:"flex",alignItems:"center",gap:4,fontFamily:"inherit" }}>← Geri dön</button>
        <div style={{ fontSize:10,color:T.mutedL,letterSpacing:2,textTransform:"uppercase",marginBottom:18 }}>{config.subtitle}</div>
        {config.roles&&(
          <div style={{ display:"flex",gap:4,marginBottom:18,background:T.surfaceAlt,borderRadius:10,padding:4,border:`1px solid ${T.border}` }}>
            {config.roles.map((r:any)=>(
              <button key={r.val} onClick={()=>setRole(r.val)} style={{ flex:1,padding:"8px 0",borderRadius:8,border:"none",cursor:"pointer",fontWeight:600,fontSize:11,background:role===r.val?config.accent:T.surface,color:role===r.val?"#fff":T.muted,transition:"all .2s",fontFamily:"inherit" }}>{r.label}</button>
            ))}
          </div>
        )}
        {config.fields.map((f:any)=>(
          <div key={f.key} style={{ marginBottom:12 }}>
            <label style={{ fontSize:10,fontWeight:600,color:T.mutedL,display:"block",marginBottom:5,letterSpacing:1,textTransform:"uppercase" }}>{f.label}</label>
            <input value={fields[f.key]||""} onChange={e=>setFields((p:any)=>({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} type={f.type||"text"} style={inpStyle}/>
          </div>
        ))}
        {err&&<div style={{ fontSize:12,color:T.danger,marginBottom:8 }}>{err}</div>}
        <div style={{ marginBottom:14 }}/>
        <button onClick={handle} disabled={loading} style={{ width:"100%",background:loading?T.border:config.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px 0",fontWeight:600,fontSize:14,cursor:loading?"default":"pointer",transition:"all .2s",letterSpacing:.2,fontFamily:"inherit" }}>{loading?"Doğrulanıyor...":"Giriş Yap"}</button>
        <div style={{ marginTop:14,padding:"11px 13px",background:config.accent+"08",borderRadius:10,border:`1px solid ${config.accent}20` }}>
          <div style={{ fontSize:11,color:config.accent,fontWeight:600,marginBottom:4 }}>Güvenlik Notu</div>
          <div style={{ fontSize:11,color:T.muted,lineHeight:1.7 }}>{config.secNote}</div>
        </div>
        {config.role==="pro" && onRegister && (
          <div style={{ marginTop:16,textAlign:"center",paddingTop:14,borderTop:`1px solid ${T.border}` }}>
            <span style={{ fontSize:13,color:T.muted }}>Hesabınız yok mu? </span>
            <button onClick={onRegister} style={{ fontSize:13,color:config.accent,fontWeight:700,background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit",textDecoration:"underline" }}>
              Kayıt Ol
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// HAKKIMIZDA MODAL
// ─────────────────────────────────────────────────────────────
const HakkimizdaModal = ({ onClose }:any) => (
  <div style={{ position:"fixed",inset:0,background:"rgba(20,30,40,.55)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{ background:T.surface,borderRadius:20,width:"100%",maxWidth:680,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(20,30,40,.18)",fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ padding:"24px 28px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:22,fontWeight:700,color:T.text,letterSpacing:-.4 }}>Hakkımızda</div>
          <div style={{ fontSize:12,color:T.muted,marginTop:3 }}>AnımsamA · Digital Social Services (DSS)</div>
        </div>
        <button onClick={onClose} style={{ background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:8,width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:T.muted,flexShrink:0 }}>✕</button>
      </div>
      {/* Content */}
      <div style={{ padding:"24px 28px 28px" }}>
        {/* Platform tanımı */}
        <div style={{ background:`linear-gradient(135deg,${T.greenL},#EFF7EC)`,border:`1px solid ${T.green}30`,borderRadius:14,padding:"18px 20px",marginBottom:20 }}>
          <div style={{ fontWeight:700,fontSize:14,color:T.greenD,marginBottom:8 }}>AnımsamA Platformu</div>
          <p style={{ fontSize:13,color:T.text,lineHeight:1.85,margin:0 }}>
            Anımsa, <strong>Digital Social Services (DSS)</strong> tarafından geliştirilen, dijital sosyal hizmet uygulamaları ile akademik araştırmaları bütünleştirmeyi amaçlayan yenilikçi bir dijital platformdur. Proje, <strong>Prof. Dr. Umut Yanardağ</strong> yürütücülüğünde tasarlanmış olup, sosyal hizmet alanında teknoloji temelli müdahale ve değerlendirme modellerinin geliştirilmesine katkı sunmayı hedeflemektedir.
          </p>
        </div>
        {/* DSS */}
        <div style={{ background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:20 }}>
          <div style={{ fontWeight:700,fontSize:14,color:T.primary,marginBottom:8 }}>Digital Social Services (DSS)</div>
          <p style={{ fontSize:13,color:T.text,lineHeight:1.85,margin:0 }}>
            DSS, sosyal hizmet uygulamalarını dijital teknolojiler, yapay zekâ sistemleri ve veri temelli analiz yaklaşımlarıyla destekleyen bir araştırma ve geliştirme girişimidir. DSS'in temel yaklaşımı; insan odaklı hizmet anlayışını koruyarak dijital araçların <strong>etik, güvenli ve bilimsel temelde</strong> kullanılmasını sağlamaktır.
          </p>
        </div>
        {/* İki aşama yan yana */}
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20 }}>
          <div style={{ background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
              <div style={{ width:24,height:24,borderRadius:8,background:T.primary+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12 }}>🔬</div>
              <div style={{ fontWeight:700,fontSize:13,color:T.text }}>Faz I — Araştırma</div>
            </div>
            <p style={{ fontSize:12,color:T.muted,lineHeight:1.8,margin:0 }}>
              Anımsa ilk aşamada akademik araştırma amaçlıdır: yapılandırılmış anımsama çalışmaları, müdahale süreçleri ve kullanıcı deneyimi analizi. Toplanan veriler yalnızca <strong>açık rıza ve etik kurul onayı</strong> çerçevesinde, anonimleştirilmiş biçimde bilimsel çalışmalarda değerlendirilecektir.
            </p>
          </div>
          <div style={{ background:T.surfaceAlt,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
              <div style={{ width:24,height:24,borderRadius:8,background:T.family+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12 }}>🏥</div>
              <div style={{ fontWeight:700,fontSize:13,color:T.text }}>Faz II — Profesyonel Platform</div>
            </div>
            <p style={{ fontSize:12,color:T.muted,lineHeight:1.8,margin:0 }}>
              İkinci aşamada Anımsa; ruh sağlığı profesyonelleri, sosyal hizmet uzmanları ve danışanlar için <strong>güvenli, etik ve yapılandırılmış</strong> bir dijital müdahale ortamına dönüştürülecektir.
            </p>
          </div>
        </div>
        {/* Temel ilkeler */}
        <div style={{ background:`linear-gradient(135deg,#F8F6FF,${T.surface})`,border:`1px solid ${T.family}30`,borderRadius:14,padding:"16px 20px",marginBottom:20 }}>
          <div style={{ fontWeight:700,fontSize:13,color:T.family,marginBottom:10 }}>Temel İlkeler</div>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {["Bilimsel Titizlik","Veri Güvenliği","Etik İlkelere Bağlılık","İnsan Onuruna Saygı","Disiplinlerarası İş Birliği","Akademik Araştırma"].map((p,i)=>(
              <span key={i} style={{ fontSize:11,fontWeight:600,background:T.family+"10",color:T.family,border:`1px solid ${T.family}25`,borderRadius:20,padding:"4px 12px" }}>{p}</span>
            ))}
          </div>
        </div>
        {/* Sonuç */}
        <p style={{ fontSize:13,color:T.muted,lineHeight:1.85,margin:0,paddingTop:16,borderTop:`1px solid ${T.border}` }}>
          Anımsa, <strong>bilimsel titizlik, veri güvenliği, etik ilkelere bağlılık ve insan onuruna saygı</strong> temelinde geliştirilmiş bir dijital sosyal hizmet girişimidir. Platformun gelişim süreci; akademik araştırma, uygulama deneyimi ve disiplinlerarası iş birliği ile sürdürülecektir.
        </p>
        {/* İletişim */}
        <div style={{ marginTop:20,paddingTop:16,borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12 }}>
          <div style={{ width:36,height:36,borderRadius:10,background:T.greenL,border:`1px solid ${T.green}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0 }}>✉️</div>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:T.muted,letterSpacing:.5,textTransform:"uppercase",marginBottom:2 }}>İletişim</div>
            <a href="mailto:umutyanardag@gmail.com" style={{ fontSize:14,fontWeight:600,color:T.green,textDecoration:"none",letterSpacing:.2 }}>umutyanardag@gmail.com</a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ROL SEÇİM EKRANI — Terapist sağ üstte küçük sign-in butonu
// ─────────────────────────────────────────────────────────────
const RoleSelect = ({ onSelect }:any) => {
  const [hov,setHov]=useState<string|null>(null);
  const [showAbout,setShowAbout]=useState(false);
  return (
    <div style={{ minHeight:"100vh",background:"linear-gradient(145deg,#E2E2E6,#EBEBEB)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 20px",fontFamily:"'DM Sans',system-ui,sans-serif",position:"relative" }}>
      <GlobalStyle/>
      {showAbout && <HakkimizdaModal onClose={()=>setShowAbout(false)}/>}

      {/* ── Üst sağ — Hakkımızda + Terapist butonu ── */}
      <div style={{ position:"absolute",top:20,right:24,display:"flex",alignItems:"center",gap:10 }}>
        <button
          onClick={()=>setShowAbout(true)}
          onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background=T.surfaceAlt;}}
          onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background="transparent";}}
          style={{ display:"flex",alignItems:"center",gap:6,background:"transparent",border:`1.5px solid ${T.border}`,borderRadius:10,padding:"8px 16px",cursor:"pointer",transition:"all .2s",fontSize:13,fontWeight:600,color:T.muted,fontFamily:"inherit" }}>
          Hakkımızda
        </button>
        <button
          onClick={()=>onSelect("pro")}
          onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background=T.greenL;(e.currentTarget as HTMLButtonElement).style.borderColor=T.green+"80";}}
          onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background=T.surface;(e.currentTarget as HTMLButtonElement).style.borderColor=T.border;}}
          style={{ display:"flex",alignItems:"center",gap:8,background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"8px 16px",cursor:"pointer",transition:"all .2s",boxShadow:"0 1px 4px rgba(30,45,61,.08)" }}>
          <StatusDot color={T.green}/>
          <span style={{ fontSize:13,fontWeight:600,color:T.textSub }}>Terapist / Uzman</span>
          <span style={{ fontSize:12,color:T.mutedL }}>→</span>
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign:"center",marginBottom:44 }}>
        <div style={{ fontSize:36,fontWeight:700,color:T.text,letterSpacing:-1,marginBottom:8 }}>Anımsam<span style={{ color:T.green }}>A</span></div>
        <div style={{ fontSize:13,color:T.muted,letterSpacing:.5 }}>Çevrimiçi Anımsama Terapisi Platformu</div>
      </div>

      {/* ── Danışan + Aile — merkez ── */}
      <div className="roles-grid">
        {[
          { key:"client",title:"Danışan",accent:T.primary,desc:"Terapistinizin rehberliğinde yaşam hikayenizi keşfedin ve anılarınızı paylaşın.",items:["Anı çizelgesi","Görüntülü seans","Aile mesajları","Duygu günlüğü"] },
          { key:"family",title:"Aile Üyesi",accent:T.family,desc:"Sesli mesajlar ve fotoğraflarınız ile sevdiklerinizin terapi yolculuğuna katkı sağlayın.",items:["Sesli mesaj","Fotoğraf paylaşma","Terapisti bilgilendirme","Görüşmeye katılım"] },
        ].map(r=>(
          <div key={r.key} onMouseEnter={()=>setHov(r.key)} onMouseLeave={()=>setHov(null)} onClick={()=>onSelect(r.key)}
            style={{ background:hov===r.key?T.surface:T.surfaceAlt,border:`1.5px solid ${hov===r.key?r.accent+"60":T.border}`,borderRadius:16,padding:24,cursor:"pointer",transition:"all .25s",transform:hov===r.key?"translateY(-4px)":"none",boxShadow:hov===r.key?`0 16px 48px ${r.accent}18`:"0 1px 4px rgba(30,45,61,.05)" }}>
            <div style={{ width:40,height:40,borderRadius:12,background:r.accent+"15",border:`1.5px solid ${r.accent}30`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14 }}>
              <span style={{ fontSize:18,color:r.accent }}>◈</span>
            </div>
            <div style={{ fontWeight:600,fontSize:17,color:T.text,marginBottom:6 }}>{r.title}</div>
            <div style={{ fontSize:12,color:T.muted,lineHeight:1.7,marginBottom:16 }}>{r.desc}</div>
            <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:14,marginBottom:16 }}>
              {r.items.map((item,i)=><div key={i} style={{ display:"flex",alignItems:"center",gap:8,marginBottom:7 }}><div style={{ width:4,height:4,borderRadius:"50%",background:r.accent,flexShrink:0 }}/><span style={{ fontSize:12,color:T.muted }}>{item}</span></div>)}
            </div>
            <div style={{ background:r.accent,color:"#fff",borderRadius:10,padding:"10px 0",textAlign:"center",fontWeight:600,fontSize:13 }}>Giriş Yap →</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ textAlign:"center",marginTop:40 }}>
        <div style={{ display:"flex",gap:20,justifyContent:"center",flexWrap:"wrap",marginBottom:12 }}>
          {[{c:T.green,t:"Güvenli Bağlantı"},{c:T.primary,t:"KVKK / GDPR"},{c:T.amber,t:"Seans Gizliliği"}].map((x,i)=>(
            <div key={i} style={{ display:"flex",alignItems:"center",gap:6 }}><StatusDot color={x.c}/><span style={{ fontSize:11,color:T.muted,fontWeight:500 }}>{x.t}</span></div>
          ))}
        </div>
        <div style={{ fontSize:11,color:T.mutedL }}>© {new Date().getFullYear()} AnımsamA · Her Hakkı Saklıdır · DSS</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ANA UYGULAMA
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ŞİFRE SIFIRLAMA EKRANI
// ─────────────────────────────────────────────────────────────
const ResetPassword = ({ onDone }:any) => {
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const handle = async () => {
    if(pass.length < 8){ setErr("En az 8 karakter gerekli."); return; }
    if(pass !== pass2){ setErr("Şifreler eşleşmiyor."); return; }
    setLoading(true); setErr("");
    try {
      const { error } = await supabase.auth.updateUser({ password: pass });
      if(error) throw error;
      setOk(true);
      setTimeout(()=>onDone(), 2000);
    } catch(e:any){
      setErr(e.message || "Şifre güncellenemedi.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh", background:"#F4F5F7", display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <div style={{ background:"#fff", borderRadius:18, padding:36, maxWidth:420, width:"100%", boxShadow:"0 4px 24px rgba(30,45,61,.08)" }}>
        <div style={{ fontSize:24, fontWeight:800, color:"#1A2332", letterSpacing:-1, marginBottom:6 }}>Anımsam<span style={{ color:"#2D9E6B" }}>A</span></div>
        <div style={{ fontSize:16, fontWeight:600, marginBottom:20, color:"#1A2332" }}>Yeni Şifre Belirleyin</div>
        {ok ? (
          <div style={{ textAlign:"center", color:"#2D9E6B", fontWeight:600, fontSize:15 }}>✓ Şifreniz güncellendi, yönlendiriliyorsunuz...</div>
        ) : (
          <>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, fontWeight:600, color:"#6B7A90", letterSpacing:.4, textTransform:"uppercase", display:"block", marginBottom:6 }}>Yeni Şifre</label>
              <input type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="En az 8 karakter"
                style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #E4E7EC", borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit" }}/>
            </div>
            <div style={{ marginBottom:18 }}>
              <label style={{ fontSize:11, fontWeight:600, color:"#6B7A90", letterSpacing:.4, textTransform:"uppercase", display:"block", marginBottom:6 }}>Şifre Tekrar</label>
              <input type="password" value={pass2} onChange={e=>setPass2(e.target.value)} placeholder="Şifrenizi tekrar girin"
                style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #E4E7EC", borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit" }}/>
            </div>
            {err && <div style={{ fontSize:12, color:"#DC2626", marginBottom:12 }}>⚠ {err}</div>}
            <button onClick={handle} disabled={loading} style={{ width:"100%", padding:"13px 0", background:"#2D9E6B", color:"#fff", border:"none", borderRadius:12, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
              {loading ? "Kaydediliyor..." : "Şifremi Güncelle"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function App() {
  const [screen,setScreen]=useState("role"); const [user,setUser]=useState<any>(null); const [meetCode,setMeetCode]=useState("");

  // Supabase auth token'larını URL'den yakala (şifre sıfırlama vb.)
  useEffect(()=>{
    const hash = window.location.hash;
    if(hash.includes("type=recovery")){
      setScreen("resetPassword");
      return;
    }
    // Oturum kontrolü
    if(supabase){
      supabase.auth.getSession().then(({ data }:any)=>{
        if(data?.session?.user){
          const u = data.session.user;
          supabase.from("profiles").select("*").eq("id", u.id).single().then(({ data:profile }:any)=>{
            if(profile){
              setUser({ ...u, name:profile.name, role:profile.role });
              setScreen(profile.role==="family"?"familyDash":profile.role==="client"?"clientDash":"proDash");
            }
          });
        }
      });
    }
  },[]);

  const login=async(u:any)=>{
    setUser(u);
    setScreen(u.role==="family"?"familyDash":u.role==="client"?"clientDash":"proDash");
  };

  const logout=async()=>{
    try{ if(supabase) await supabase.auth.signOut(); }catch(e){}
    setUser(null); setScreen("role");
  };

  if(screen==="resetPassword") return <ResetPassword onDone={()=>{ window.location.hash=""; setScreen("proLogin"); }}/>;
  if(screen==="role")       return <RoleSelect onSelect={(r:string)=>setScreen(r+"Login")}/>;
  if(screen==="proRegister") return <TherapistRegister onBack={()=>setScreen("proLogin")} onSuccess={()=>setScreen("proLogin")}/>;
  if(screen==="proLogin")   return <LoginForm config={LOGIN_CONFIGS.pro}    onLogin={login} onBack={()=>setScreen("role")} onRegister={()=>setScreen("proRegister")}/>;
  if(screen==="clientLogin")return <LoginForm config={LOGIN_CONFIGS.client} onLogin={login} onBack={()=>setScreen("role")}/>;
  if(screen==="familyLogin")return <LoginForm config={LOGIN_CONFIGS.family} onLogin={login} onBack={()=>setScreen("role")}/>;
  if(screen==="proDash")    return <ProDash    user={user} onLogout={logout} meetCode={meetCode} onMeetCode={setMeetCode}/>;
  if(screen==="clientDash") return <ClientDash user={user} onLogout={logout} meetCode={meetCode}/>;
  if(screen==="familyDash") return <FamilyDash user={user} onLogout={logout} meetCode={meetCode}/>;
  return null;
}
